import React, { useEffect, useMemo, useRef, useState } from "react";
import Pagination from "react-js-pagination";
import ReactDatePicker from "react-datepicker";
import JoditEditor from "jodit-react";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from "@mui/icons-material/Close";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import HistoryIcon from "@mui/icons-material/History";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import { Box, IconButton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ModalBox from "../../components/modal/ModalBox";
import NotFound from "../../components/not-found/NotFound";
import { Input, TextArea } from "../../components/Form";
import Switch from "../../components/Switch/Switch";
import { CustomTabs, CustomTab } from "../../components/tabs/Tabs";
import ResponsiveDetailPage from "../../components/ResponsiveDetailPage";
import useDebounce from "../../hooks/useDebounce";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useButtonLoader from "../../hooks/UseButtonLoader";
import findSerialNumber from "../../utils/findSerialNumber";
import { toastMessage } from "../../utils/toastMessage";
import common from "../../routes/common";
import { consultant as consultantRoutes } from "../../routes/route";
import ConsultantReviewsListing from "./ConsultantReviewsListing";
import {
  addPreSelectedConsultantApi,
  approveConsultantStatus,
  changeConsultantStatus,
  changeConsultantStatusisFake,
  fetchPreSelectedConsultantApi,
  fetchPreSelectedConsultantEmailTemplatesApi,
  fetchPreSelectedConsultantSubmittedFormsApi,
  sendPreSelectedConsultantPersonalizedEmailApi,
  sendPreSelectedConsultantCorrectionMailApi,
  sendPreSelectedConsultantSubmittedCommunicationApi,
  updatePreSelectedConsultantEmailTemplateApi,
  updatePreSelectedConsultantInterviewDetailsApi,
  updatePreSelectedConsultantInterviewStatusApi,
  updatePreSelectedConsultantApi,
  deletePreSelectedConsultantApi,
  updatePreSelectedConsultantAssetReviewApi,
  updatePreSelectedConsultantStage3Api,
  updatePreSelectedConsultantSubmittedConsultantDataApi,
  updatePreSelectedConsultantSubmittedOnboardingRowApi,
  submitManualPreSelectedConsultantOnboardingApi,
  moveConsultantToBinApi,
  reschedulePreSelectedConsultantInterviewMailApi,
  restoreConsultantFromBinApi,
  uploadPreSelectedConsultantSheetApi,
  addReviewByAdminApi,
} from "../../services/consultantManagement";
import { requestAcceptRejectApi } from "../../services/requestManagement";
import { SelectedConsultantFormContent } from "./SelectedConsultantForm";

const { NOT_FOUND } = common;
const { CONSULTANT_DETAIL, CONSULTANT_EDIT, CONSULTANT_EDIT_V1, PRE_SELECTED_CONSULTANT } = consultantRoutes;

const defaultForm = {
  name: "",
  email: "",
  phone: "",
  category: "",
  source: "",
  interviewDateTime: null,
  scheduleEmailAt: null,
};

const formatDateTime = (value) => {
  if (!value) return "---";
  return new Date(value).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatInputDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const ymd = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const startOfLocalDay = (value) => {
  const date = parseLocalDate(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfLocalDay = (value) => {
  const date = parseLocalDate(value);
  if (!date) return null;
  date.setHours(23, 59, 59, 999);
  return date;
};

const toDateFilterParam = (value) => {
  const date = parseLocalDate(value);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDatePresetRange = (preset) => {
  const todayStart = startOfLocalDay(new Date());
  const todayEnd = endOfLocalDay(new Date());
  if (preset === "today") {
    return { startDate: todayStart, endDate: todayEnd };
  }
  if (preset === "last7days") {
    const start = startOfLocalDay(new Date());
    start.setDate(start.getDate() - 6);
    return { startDate: start, endDate: todayEnd };
  }
  if (preset === "next7days") {
    const end = endOfLocalDay(new Date());
    end.setDate(end.getDate() + 6);
    return { startDate: todayStart, endDate: end };
  }
  return { startDate: null, endDate: null };
};

const isSameCalendarDay = (left, right) => {
  const start = startOfLocalDay(left);
  const end = startOfLocalDay(right);
  return Boolean(start && end && start.getTime() === end.getTime());
};

const getActiveDatePreset = (startDate, endDate, presets = ["today", "last7days", "next7days"]) => {
  if (!startDate || !endDate) return "";
  return presets.find((preset) => {
    const range = getDatePresetRange(preset);
    return isSameCalendarDay(startDate, range.startDate) && isSameCalendarDay(endDate, range.endDate);
  }) || "custom";
};

const clampRangeToToday = (startDate, endDate) => {
  const todayStart = startOfLocalDay(new Date());
  const todayEnd = endOfLocalDay(new Date());
  let start = startOfLocalDay(startDate);
  let end = endOfLocalDay(endDate);
  if (start && start.getTime() > todayEnd.getTime()) start = todayStart;
  if (end && end.getTime() > todayEnd.getTime()) end = todayEnd;
  if (start && end && start.getTime() > end.getTime()) {
    end = endOfLocalDay(start);
  }
  return { startDate: start, endDate: end };
};

const datePresetButtonStyle = (active) => ({
  border: active ? "1px solid #111" : "1px solid #d9dee5",
  padding: "8px 11px",
  fontWeight: 600,
  background: active ? "#111" : "#fff",
  color: active ? "#fff" : "#111",
  minWidth: 58,
});

const escapePreviewHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const buildPersonalMailPreviewVariables = (consultant = {}) => ({
  name: consultant.name || "",
  email: consultant.email || "",
  phone: consultant.phone || "",
  category: consultant.category || "",
  source: consultant.source || "",
  interviewDateTime: consultant.interviewDateTime ? formatDateTime(consultant.interviewDateTime) : "",
  scheduleEmailAt: consultant.scheduleEmailAt ? formatDateTime(consultant.scheduleEmailAt) : "",
  selectionName: consultant.selectionName || consultant.name || "",
  selectionPrice: consultant.selectionPrice || "",
  formLink: consultant.selectedFormToken ? `pre-selected-consultant-form/${consultant.selectedFormToken}` : "",
});

const renderPreviewTemplate = (template = "", variables = {}) =>
  String(template).replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (match, key) =>
    escapePreviewHtml(variables[key] ?? "")
  );

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "---";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "---";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatTopicNames = (value) => {
  let topics = value;
  if (typeof topics === "string") {
    try {
      topics = JSON.parse(topics);
    } catch (_) {
      return topics || "---";
    }
  }
  if (!Array.isArray(topics)) return "---";
  const names = [...new Set(topics.map((topic) => typeof topic === "object" ? topic.name : topic).filter(Boolean))];
  return names.join(", ") || "---";
};

const isManualCreatedConsultant = (item = {}) => {
  const source = String(item.source || item.preSelectedConsultantId?.source || "").toLowerCase();
  return (
    item.emailStatus === "manual_created" ||
    source.includes("manual created") ||
    source.includes("admin manual") ||
    (item.activityLogs || []).some((log) => log.action === "manual_onboarding_created")
  );
};

const ManualCreatedBadge = () => (
  <span style={{ color: "#5f8fd6", fontWeight: 700, whiteSpace: "nowrap" }}>
    Manual Created
  </span>
);

const hiddenSubmittedFormFields = new Set(["selectionId", "token"]);
const lockedConsultantEditFields = new Set(["consultantUserId"]);
const editableDocumentFields = [
  "profileImage",
  "aadharFront",
  "aadharBack",
  "panCardFront",
  "marksheet10th",
  "certificateOfCompletion",
  "bankProof",
  "videoIntroduction",
  "galleryPhotos",
];

const templateLabels = {
  interview: "Interview Mail",
  selected: "Selected Mail",
  rejected: "Rejected Mail",
};

const templatePlaceholders = {
  interview: [
    { label: "Name", value: "{{name}}" },
    { label: "Email", value: "{{email}}" },
    { label: "Phone", value: "{{phone}}" },
    { label: "Category", value: "{{category}}" },
    { label: "Source", value: "{{source}}" },
    { label: "Interview Time", value: "{{interviewDateTime}}" },
  ],
  selected: [
    { label: "Name", value: "{{name}}" },
    { label: "Email", value: "{{email}}" },
    { label: "Phone", value: "{{phone}}" },
    { label: "Category", value: "{{category}}" },
    { label: "Source", value: "{{source}}" },
    { label: "Selected Name", value: "{{selectionName}}" },
    { label: "Price", value: "{{selectionPrice}}" },
    { label: "Form Link", value: "{{formLink}}" },
  ],
  rejected: [
    { label: "Name", value: "{{name}}" },
    { label: "Email", value: "{{email}}" },
    { label: "Phone", value: "{{phone}}" },
    { label: "Category", value: "{{category}}" },
    { label: "Source", value: "{{source}}" },
  ],
};

const personalMailPlaceholders = [
  { label: "Name", value: "{{name}}" },
  { label: "Email", value: "{{email}}" },
  { label: "Phone", value: "{{phone}}" },
  { label: "Category", value: "{{category}}" },
  { label: "Source", value: "{{source}}" },
  { label: "Interview Time", value: "{{interviewDateTime}}" },
  { label: "Schedule Time", value: "{{scheduleEmailAt}}" },
  { label: "Selected Name", value: "{{selectionName}}" },
  { label: "Price", value: "{{selectionPrice}}" },
  { label: "Form Link", value: "{{formLink}}" },
];

const requiredPersonalFields = [
  "name",
  "email",
  "userName",
  "dateOfBirth",
  "gender",
  "profileBio",
  "primaryNumber",
  "highestEducationalQualification",
  "currentProfession",
  "primarySkill",
  "learningSource",
  "experienceYears",
  "dailyWorkHours",
  "expertise",
  "preferLanguage",
  "topics",
  "address",
  "city",
  "state",
  "pincode",
  "heardAboutVedicMeet",
  "otherOnlinePlatformWork",
  "favouriteSong",
  "zodiacSign",
  "movieGenre",
  "hobbies",
  "placeOfBirth",
  "highestQualification",
  "minimumEarningExpectation",
  "referredToVedicMeet",
  "fullTimeJob",
  "panCardNumber",
  "aadharCardNumber",
  "accountNumber",
  "ifscCode",
  "bankName",
  "bankBranch",
];

const requiredDocumentFields = [
  "profileImage",
  "aadharFront",
  "aadharBack",
  "panCardFront",
  "marksheet10th",
  "certificateOfCompletion",
  "bankProof",
];

const mandatoryStage3Fields = [
  "registrationDate",
  "liveDate",
  "userId",
  "userName",
  "priceSource",
  "type",
];

const requiredDocumentLabels = {
  profileImage: "Profile Photo",
  aadharFront: "Aadhar Front",
  aadharBack: "Aadhar Back",
  panCardFront: "PAN Card Front",
  marksheet10th: "10th Marksheet",
  certificateOfCompletion: "Certificate of Completion",
  bankProof: "Bank Proof",
  videoIntroduction: "Introduction Video",
};

const richTextEditorConfig = {
  readonly: false,
  toolbar: true,
  placeholder: "Type email content here",
  spellcheck: true,
  language: "en",
  toolbarButtonSize: "medium",
  showCharsCounter: true,
  showWordsCounter: true,
  showXPathInStatusbar: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  buttons: [
    "bold",
    "italic",
    "underline",
    "strikethrough",
    "|",
    "fontsize",
    "paragraph",
    "|",
    "ul",
    "ol",
    "|",
    "link",
    "table",
    "|",
    "align",
    "undo",
    "redo",
    "|",
    "eraser",
  ],
};

const getFileNameFromUrl = (url, fallback = "document") => {
  try {
    const parsed = new URL(url);
    const pathname = decodeURIComponent(parsed.pathname || "");
    return pathname.split("/").filter(Boolean).pop() || fallback;
  } catch {
    return fallback;
  }
};

const downloadFile = async (url, fallbackName) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("File download failed");

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = getFileNameFromUrl(url, fallbackName);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const downloadTextFile = (content, fileName, mimeType = "text/csv;charset=utf-8;") => {
  const blob = new Blob([content], { type: mimeType });
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const csvEscape = (value) => {
  const text = formatValue(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const preSelectedSheetTemplateCsv = [
  ["Name", "Email", "Phone Number", "Category", "Source", "Interview Date & Time", "Schedule Email Time"],
  ["Gautam", "gautam@example.com", "9876543210", "Astrology", "Instagram", "2026-05-15 14:30", "2026-05-14 10:00"],
]
  .map((row) => row.map(csvEscape).join(","))
  .join("\n");

const interviewConductOptions = [
  { label: "Pending", value: "pending" },
  { label: "Done", value: "done" },
  { label: "Not Connected", value: "not_connected" },
  { label: "Rescheduled", value: "rescheduled" },
];

const interviewStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Selected", value: "selected" },
  { label: "Rejected", value: "rejected" },
];

const interviewEmailStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Processing", value: "processing" },
  { label: "Sent", value: "sent" },
  { label: "Failed", value: "failed" },
  { label: "Manual Created", value: "manual_created" },
];

const selectionMailStatusOptions = [
  { label: "Sent", value: "sent" },
  { label: "Not Sent", value: "not_sent" },
];

const onboardingStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Accept", value: "accept" },
  { label: "Reject", value: "reject" },
  { label: "N/A", value: "na" },
];

const onboardingYesNoOptions = [
  { label: "Pending", value: "pending" },
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const onboardingSentOptions = [
  { label: "Not Sent", value: "not_sent" },
  { label: "Sent", value: "sent" },
];

const onboardingFinalStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Live", value: "live" },
];

const getOnboardingControlValue = (item = {}, key, fallback = "") =>
  item.onboardingControls?.[key] ?? fallback;

const interviewColumns = [
  { key: "serial", label: "S. No.", alwaysVisible: true },
  { key: "interviewDate", label: "Interview Date" },
  { key: "source", label: "Source" },
  { key: "name", label: "Name" },
  { key: "contact", label: "Contact No." },
  { key: "email", label: "E-Mail Id" },
  { key: "consultantType", label: "Consultant Type" },
  { key: "interviewConduct", label: "Interview Conduct" },
  { key: "selectionStatus", label: "Selection Status" },
  { key: "selectionReason", label: "Selection & Rejection Reason" },
  { key: "interviewScore", label: "Interview Score" },
  { key: "usp", label: "USP" },
  { key: "pricePerMin", label: "Price/min" },
  { key: "saveDetails", label: "Save Details" },
  { key: "mailSent", label: "Mail Sent" },
  { key: "selectionMail", label: "Selection Mail" },
  { key: "scheduleEmail", label: "Schedule Email Time" },
  { key: "personalMail", label: "Personal Mail" },
  { key: "action", label: "Action", alwaysVisible: true },
];

const submittedColumns = [
  { key: "select", label: "Select", alwaysVisible: true },
  { key: "serial", label: "S. No.", alwaysVisible: true },
  { key: "date", label: "Date" },
  { key: "candidateName", label: "Candidate Name" },
  { key: "contact", label: "Contact No." },
  { key: "email", label: "E-Mail Id" },
  { key: "selectionMail", label: "Selection Mail" },
  { key: "selectionCallDate", label: "Selection Call Date" },
  { key: "selectionCallDone", label: "Selection Call Done" },
  { key: "profilePhoto", label: "Profile Photo" },
  { key: "galleryPhoto", label: "Gallery Photo" },
  { key: "profileDetail", label: "Profile Detail" },
  { key: "professionalDetail", label: "Professional Detail" },
  { key: "addressDetails", label: "Address Details" },
  { key: "personalPreferences", label: "Personal Preferences" },
  { key: "additionalQuestions", label: "Additional Questions" },
  { key: "document", label: "Document" },
  { key: "bankDetails", label: "Bank Details" },
  { key: "idCreatedDate", label: "ID Created Date" },
  { key: "idCreated", label: "ID Created" },
  { key: "idLiveDate", label: "ID Live Date" },
  { key: "idLive", label: "ID Live" },
  { key: "agreementDate", label: "Agreement Date" },
  { key: "agreementSign", label: "Agreement Sign" },
  { key: "agreementUpload", label: "Agreement Upload" },
  { key: "inductionDate", label: "Induction Date" },
  { key: "induction", label: "Induction" },
  { key: "finalStatus", label: "Final Status" },
  { key: "save", label: "Save" },
  { key: "action", label: "Action", alwaysVisible: true },
];

const consultantDataColumns = [
  { key: "serial", label: "S. No.", alwaysVisible: true },
  { key: "profile", label: "Profile" },
  { key: "consultantId", label: "ID" },
  { key: "candidateName", label: "Name" },
  { key: "userName", label: "User Name" },
  { key: "contact", label: "Phone Number" },
  { key: "consultantType", label: "Type" },
  { key: "gender", label: "Gender" },
  { key: "registrationDate", label: "Registration Date" },
  { key: "profileComplete", label: "Profile Complete" },
  { key: "statusDate", label: "Status Date" },
  { key: "action", label: "Action", alwaysVisible: true },
];

const allSubmittedColumnKeys = Array.from(
  new Set([...submittedColumns, ...consultantDataColumns].map((column) => column.key))
);

const getConsultantRecordId = (details) => {
  const consultant = details?.consultantId;
  if (!consultant) return details?.formData?.consultantId || "";
  return typeof consultant === "object" ? consultant._id || "" : consultant;
};

const getConsultantUserId = (details) => {
  const consultant = details?.consultantId;
  if (!consultant || typeof consultant !== "object") return details?.formData?.consultantUserId || "";
  return consultant.userId || details?.formData?.consultantUserId || "";
};

const buildDisplayFormData = (details) => {
  const formData = details?.formData || {};
  const consultantRecordId = getConsultantRecordId(details);
  const consultantUserId = getConsultantUserId(details);
  const nextFormData = Object.fromEntries(
    Object.entries(formData).filter(([key]) => !hiddenSubmittedFormFields.has(key))
  );

  if (consultantRecordId) nextFormData.consultantId = consultantRecordId;
  if (consultantUserId) nextFormData.consultantUserId = consultantUserId;

  return nextFormData;
};

const downloadSubmittedForm = (details) => {
  const formData = buildDisplayFormData(details);
  const documents = details?.documents || {};
  const rows = [["Section", "Field", "Value"]];

  Object.entries(formData).forEach(([key, value]) => {
    rows.push(["Form Data", key.replace(/([A-Z])/g, " $1"), formatValue(value)]);
  });

  Object.entries(documents).forEach(([key, value]) => {
    rows.push(["Documents", key.replace(/([A-Z])/g, " $1"), formatValue(value)]);
  });

  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
  const safeName = String(formData.name || formData.email || "submitted-form").replace(/[^a-z0-9-_]+/gi, "-");
  downloadTextFile(csv, `${safeName}-submitted-form.csv`);
};

const humanizeKey = (key = "") => String(key).replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

const isFilled = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && String(value).trim() !== "";
};

const getCompletionStats = (source = {}, requiredFields = []) => {
  const completed = requiredFields.filter((field) => isFilled(source[field]));
  const missing = requiredFields.filter((field) => !isFilled(source[field]));
  const total = requiredFields.length;
  const percent = total ? Math.round((completed.length / total) * 100) : 0;
  return { completed: completed.length, missing, total, percent };
};

const getStage3FieldValue = (field, details = {}) => {
  const formData = details.formData || {};
  const consultant = details.consultantId || {};
  const preSelected = details.preSelectedConsultantId || {};
  const values = {
    registrationDate: consultant.createdAt || consultant.created_at || details.createdAt,
    liveDate: consultant.liveDate || consultant.goLiveAt || consultant.approvedAt,
    userId: consultant.userId || formData.consultantUserId || consultant._id || formData.consultantId,
    userName: consultant.userName || consultant.username || formData.userName || formData.username || consultant.accountName || consultant.name || formData.name,
    priceSource: consultant.priceSource || consultant.price_source || formData.priceSource || preSelected.source || formData.source,
    type: "Consultant",
  };
  return values[field];
};

const getStage3MandatoryStats = (details = {}) =>
  getCompletionStats(
    mandatoryStage3Fields.reduce((acc, field) => {
      acc[field] = getStage3FieldValue(field, details);
      return acc;
    }, {}),
    mandatoryStage3Fields
  );

const buildConsultantEditData = (details = {}) => {
  const formData = details.formData || {};
  const consultant = details.consultantId || {};
  return {
    ...formData,
    name: consultant.accountName || consultant.name || formData.name || "",
    email: consultant.email || consultant.details?.email || formData.email || "",
    userName: consultant.userName || consultant.username || formData.userName || formData.username || "",
    username: consultant.userName || consultant.username || formData.userName || formData.username || "",
    primaryNumber: consultant.mobile || consultant.details?.phone || formData.primaryNumber || "",
    gender: consultant.gender || consultant.details?.gender || formData.gender || "",
    dateOfBirth: consultant.dateOfBirth || formData.dateOfBirth || "",
    profileBio: consultant.profileBio || consultant.bio || formData.profileBio || "",
    address: consultant.address || consultant.details?.address || formData.address || "",
    city: consultant.city || consultant.details?.city || formData.city || "",
    state: consultant.state || consultant.details?.state || formData.state || "",
    pincode: consultant.pincode || consultant.details?.zip || formData.pincode || "",
    placeOfBirth: consultant.placeOfBirth || formData.placeOfBirth || "",
    priceSource: consultant.priceSource || consultant.price_source || formData.priceSource || formData.source || "",
    highestEducationalQualification:
      consultant.highestEducationalQualification || formData.highestEducationalQualification || "",
    currentProfession: consultant.currentProfession || formData.currentProfession || "",
    primarySkill: consultant.primarySkill || formData.primarySkill || "",
    otherSkill: consultant.otherSkill || consultant.otherSkills || formData.otherSkill || formData.otherSkills || "",
    learningSource: consultant.learningSource || formData.learningSource || "",
    astrologyLearningSource: consultant.astrologyLearningSource || formData.astrologyLearningSource || "",
    experienceYears: consultant.experienceYears || consultant.experienceInYears || formData.experienceYears || formData.experienceInYears || "",
    dailyWorkHours: consultant.dailyWorkHours || formData.dailyWorkHours || "",
    expertise: consultant.expertise || formData.expertise || "",
    preferLanguage: consultant.preferLanguage || consultant.preferredLanguages || formData.preferLanguage || formData.preferredLanguages || "",
    highestQualification:
      consultant.highestQualification || consultant.selectedHighestQualification || formData.highestQualification || formData.selectedHighestQualification || "",
    minimumEarningExpectation: consultant.minimumEarningExpectation || formData.minimumEarningExpectation || "",
    heardAboutVedicMeet: consultant.heardAboutVedicMeet || formData.heardAboutVedicMeet || "",
    otherOnlinePlatformWork:
      consultant.otherOnlinePlatformWork || consultant.worksOnOtherPlatform || formData.otherOnlinePlatformWork || formData.worksOnOtherPlatform || "",
    otherOnlinePlatformName:
      consultant.otherOnlinePlatformName || consultant.onlinePlatformName || formData.otherOnlinePlatformName || formData.onlinePlatformName || "",
    favouriteSong: consultant.favouriteSong || formData.favouriteSong || "",
    zodiacSign: consultant.zodiacSign || formData.zodiacSign || "",
    movieGenre: consultant.movieGenre || formData.movieGenre || "",
    hobbies: consultant.hobbies || formData.hobbies || "",
    referredToVedicMeet: consultant.referredToVedicMeet || formData.referredToVedicMeet || "",
    fullTimeJob:
      consultant.fullTimeJob || consultant.currentlyWorkingFullTime || formData.fullTimeJob || formData.currentlyWorkingFullTime || "",
  };
};

const buildDisplayDocuments = (details = {}) => {
  const documents = { ...(details.documents || {}) };
  const consultant = details.consultantId || {};
  editableDocumentFields.forEach((field) => {
    if (consultant[field]) documents[field] = consultant[field];
  });
  return documents;
};

const stage3DetailTitles = {
  active: "Active Consultant Details",
  block: "Blocked Consultant Details",
  deleted: "Deleted Consultant Details",
  request: "Consultant Request Details",
};

const requestStatusLabel = (status) => {
  if (Number(status) === 2) return "Approved";
  if (Number(status) === 3) return "Rejected";
  return "Pending";
};

const requestTypeLabel = (requestType = "") =>
  requestType === "PANCARD" ? "PAN Card" : requestType === "BANK" ? "Bank" : requestType || "Request";

const statusStyle = (status) => {
  const numericStatus = Number(status);
  if (numericStatus === 2) return { color: "#198754", background: "#e8f6ef", borderColor: "#badbcc" };
  if (numericStatus === 3) return { color: "#dc3545", background: "#f8d7da", borderColor: "#f5c2c7" };
  return { color: "#8a6d00", background: "#fff3cd", borderColor: "#ffecb5" };
};

const renderDocumentLink = (value, label = "View") => {
  if (!value) return "---";
  if (!String(value).startsWith("http")) return formatValue(value);
  return (
    <>
      <a href={value} target="_blank" rel="noreferrer" className="me-2">
        {label}
      </a>
      <button
        type="button"
        className="btn btn-link p-0"
        onClick={() => downloadFile(value, label)}
      >
        Download
      </button>
    </>
  );
};

const isImageUrl = (value = "") =>
  /\.(png|jpe?g|webp|gif|bmp|avif)(\?.*)?$/i.test(String(value));

const isVideoUrl = (value = "") =>
  /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(String(value));

const MediaPreview = ({ url, type, alt }) => {
  if (!url || !String(url).startsWith("http")) return null;
  const shouldShowVideo = type === "video" || isVideoUrl(url);
  const shouldShowImage = type === "image" || isImageUrl(url);

  if (shouldShowVideo) {
    return (
      <video
        src={url}
        controls
        style={{ width: "100%", maxWidth: 360, height: 210, objectFit: "cover", borderRadius: 8, marginBottom: 10 }}
      />
    );
  }

  if (shouldShowImage) {
    return (
      <a href={url} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginBottom: 10 }}>
        <img
          src={url}
          alt={alt || "Media"}
          style={{ width: 150, height: 150, objectFit: "cover", borderRadius: 8, border: "1px solid #e9ecef" }}
        />
      </a>
    );
  }

  return null;
};

const hasRequestData = (data = {}, type) => {
  const fields = type === "BANK"
    ? ["bankName", "accountNumber", "ifsc", "bankHolderName", "bankBranch", "attachment"]
    : ["panNumber", "panImage"];
  return fields.some((field) => Boolean(data?.[field]));
};

const RequestDetailColumn = ({ title, data = {}, type }) => {
  const fields =
    type === "BANK"
      ? [
          ["bankName", "Bank Name"],
          ["accountNumber", "Account Number"],
          ["ifsc", "IFSC"],
          ["bankHolderName", "Bank Holder Name"],
          ["bankBranch", "Bank Branch"],
          ["attachment", "Bank Attachment"],
        ]
      : [
          ["panNumber", "PAN Number"],
          ["panImage", "PAN Image"],
        ];

  return (
    <div style={{ flex: "1 1 280px", minWidth: 0 }}>
      <div style={{ fontWeight: 700, marginBottom: 10, color: "#212529" }}>{title}</div>
      <div style={{ border: "1px solid #dee2e6", borderRadius: 8, overflow: "hidden" }}>
        {fields.map(([key, label]) => (
          <div
            key={key}
            style={{
              display: "grid",
              gridTemplateColumns: "145px minmax(0, 1fr)",
              gap: 12,
              padding: "10px 12px",
              borderBottom: "1px solid #edf0f2",
              background: key.includes("Image") || key === "attachment" ? "#fff" : "#fbfbfb",
            }}
          >
            <div style={{ fontWeight: 600 }}>{label}</div>
            <div style={{ minWidth: 0, wordBreak: "break-word" }}>
              {key === "attachment" || key === "panImage" ? (
                <>
                  <MediaPreview url={data?.[key]} type={isImageUrl(data?.[key]) ? "image" : undefined} alt={label} />
                  {renderDocumentLink(data?.[key], label)}
                </>
              ) : (
                formatValue(data?.[key])
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RequestReviewPanel = ({
  request,
  requestRemark,
  setRequestRemark,
  onRequestAction,
  onCorrectionMail,
  actionLoading,
  readOnlyActions,
}) => {
  const isPending = Number(request.approveStatus) === 1;
  const isApproved = Number(request.approveStatus) === 2;
  const style = statusStyle(request.approveStatus);
  const requestType = request.requestType === "PANCARD" ? "PAN" : request.requestType;
  const primaryDetails = isApproved ? request.newData || {} : request.newData || request.currentData || {};
  const previousDetails = isApproved ? request.currentData || {} : {};
  const showPreviousDetails = isApproved && hasRequestData(previousDetails, request.requestType);

  return (
    <div
      style={{
        border: "1px solid #d7dce1",
        borderRadius: 8,
        marginBottom: 18,
        overflow: "hidden",
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
          background: "#f8f9fa",
          borderBottom: "1px solid #d7dce1",
        }}
      >
        <div>
          <div style={{ fontWeight: 800 }}>{requestType} Request</div>
          <div style={{ color: "#6c757d", fontSize: 13 }}>
            Submitted: {formatDateTime(request.createdAt)}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              ...style,
              border: `1px solid ${style.borderColor}`,
              borderRadius: 999,
              padding: "5px 12px",
              fontWeight: 700,
              textTransform: "capitalize",
            }}
          >
            {requestStatusLabel(request.approveStatus)}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, padding: 14 }}>
        <RequestDetailColumn
          title={Number(request.approveStatus) === 2 ? "Approved Details" : "Submitted Details"}
          data={primaryDetails}
          type={request.requestType}
        />
        {showPreviousDetails && (
          <RequestDetailColumn
            title="Previous Request Data"
            data={previousDetails}
            type={request.requestType}
          />
        )}
      </div>

      {!readOnlyActions && isPending && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "flex-end",
            justifyContent: "space-between",
            padding: 14,
            borderTop: "1px solid #edf0f2",
            background: "#fcfcfc",
          }}
        >
          <div style={{ flex: "1 1 360px" }}>
            <label className="my-1 text-dark" style={{ fontWeight: 600 }}>
              Remark
            </label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="Remark required while rejecting"
              value={requestRemark[request._id] || request.review || ""}
              onChange={(e) =>
                setRequestRemark((prev) => ({ ...prev, [request._id]: e.target.value }))
              }
            />
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-success"
              style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}
              disabled={actionLoading}
              onClick={() => onRequestAction(request, 2)}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: 13, fontWeight: 500 }}
              disabled={actionLoading}
              onClick={() => onRequestAction(request, 3)}
            >
              Reject
            </button>
          </div>
        </div>
      )}
      {!readOnlyActions && Number(request.approveStatus) === 3 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            padding: 14,
            borderTop: "1px solid #edf0f2",
            background: "#fff8f8",
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: "#b42318" }}>
              {requestTypeLabel(request.requestType)} rejected
            </div>
            <div style={{ color: "#6c757d", fontSize: 13 }}>
              Send correction mail with the same form link. Only this section will be editable.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ fontSize: 13, fontWeight: 500 }}
            disabled={actionLoading}
            onClick={() => onCorrectionMail(request)}
          >
            Send Correction Mail
          </button>
        </div>
      )}
    </div>
  );
};

const CorrectionHistoryPanel = ({ logs = [] }) => {
  if (!logs.length) return null;

  return (
    <div
      style={{
        border: "1px solid #d7dce1",
        borderRadius: 8,
        marginBottom: 18,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 14px",
          background: "#f8f9fa",
          borderBottom: "1px solid #d7dce1",
          fontWeight: 800,
        }}
      >
        Correction History
      </div>
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {logs.map((log, index) => (
          <div
            key={`${log.createdAt || index}-${log.action || "log"}`}
            style={{
              border: "1px solid #edf0f2",
              borderRadius: 6,
              padding: "10px 12px",
              background: "#fcfcfc",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 700 }}>{humanizeKey(log.action || "correction_log")}</div>
              <div style={{ color: "#6c757d", fontSize: 13 }}>{formatDateTime(log.createdAt)}</div>
            </div>
            <div style={{ color: "#343a40", fontSize: 13, marginTop: 4 }}>
              Sections: {(log.sections || []).map((section) => humanizeKey(section)).join(", ") || "---"}
            </div>
            {log.remark ? (
              <div style={{ color: "#343a40", fontSize: 13, marginTop: 4 }}>
                Remark: {log.remark}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const CorrectionHistoryModal = ({ logs = [], onClose }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 1050,
      background: "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 18,
    }}
  >
    <div
      style={{
        width: "min(920px, 96vw)",
        maxHeight: "86vh",
        overflow: "auto",
        borderRadius: 8,
        background: "#fff",
        boxShadow: "0 18px 50px rgba(15, 23, 42, 0.22)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "14px 16px",
          borderBottom: "1px solid #edf0f2",
          background: "#f8f9fa",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Correction History</div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          Close
        </button>
      </div>
      <div style={{ padding: 16 }}>
        {logs.length ? (
          <CorrectionHistoryPanel logs={logs} />
        ) : (
          <div style={{ border: "1px solid #d7dce1", borderRadius: 8, padding: 14 }}>
            No correction history found
          </div>
        )}
      </div>
    </div>
  </div>
);

const assetStatusLabel = (status) => {
  if (status === "approved") return "Approved";
  if (status === "rejected") return "Rejected";
  return "Pending";
};

const MediaReviewPanel = ({
  assetType,
  title,
  submitted,
  required,
  items,
  review,
  remark,
  setRemark,
  onAssetAction,
  onCorrectionMail,
  actionLoading,
  readOnlyActions,
  perItemReview = false,
}) => {
  const percent = required ? Math.min(100, Math.round((submitted / required) * 100)) : 0;
  const currentStatus = review?.status || "pending";
  const isPending = currentStatus === "pending";
  const style = statusStyle(currentStatus === "approved" ? 2 : currentStatus === "rejected" ? 3 : 1);
  const hasIndividualReviews = items.some((item) => item.review);

  return (
    <div style={{ border: "1px solid #d7dce1", borderRadius: 8, marginBottom: 18, background: "#fff" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
          background: "#f8f9fa",
          borderBottom: "1px solid #d7dce1",
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 18, color: "#2f343a" }}>{title}</div>
          <div style={{ color: "#343a40", fontSize: 14 }}>
            {submitted} / {required} submitted | {percent}%
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {currentStatus === "rejected" && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              style={{ fontSize: 13, fontWeight: 500 }}
              disabled={actionLoading}
              onClick={() => onCorrectionMail(assetType, title)}
            >
              Mail Correction
            </button>
          )}
          <span
            style={{
              ...style,
              border: `1px solid ${style.borderColor}`,
              borderRadius: 999,
              padding: "5px 12px",
              fontWeight: 600,
            }}
          >
            {assetStatusLabel(currentStatus)}
          </span>
        </div>
      </div>

      <div style={{ padding: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          {items.length ? (
            items.map((item, index) => {
              const itemStatus = item.review?.status || (perItemReview && hasIndividualReviews ? "pending" : currentStatus);
              const itemStyle = statusStyle(itemStatus === "approved" ? 2 : itemStatus === "rejected" ? 3 : 1);
              return (
              <div key={`${item.label}-${index}`} style={{ border: "1px solid #edf0f2", borderRadius: 8, padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                  <div style={{ color: "#050505", fontWeight: 600 }}>{item.label}</div>
                  {perItemReview && (
                    <span style={{ ...itemStyle, border: `1px solid ${itemStyle.borderColor}`, borderRadius: 999, padding: "3px 8px", fontSize: 12, fontWeight: 600 }}>
                      {assetStatusLabel(itemStatus)}
                    </span>
                  )}
                </div>
                <MediaPreview
                  url={item.url}
                  type={assetType === "video" ? "video" : "image"}
                  alt={item.label}
                />
                {renderDocumentLink(item.url, item.label)}
                {perItemReview && !readOnlyActions && (
                  <div className="d-flex gap-2 mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      style={{ fontSize: 12, fontWeight: 500, color: "#fff" }}
                      disabled={actionLoading || itemStatus === "approved"}
                      onClick={() => onAssetAction(assetType, "approved", item.key)}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      style={{ fontSize: 12, fontWeight: 500 }}
                      disabled={actionLoading || itemStatus === "rejected"}
                      onClick={() => onAssetAction(assetType, "rejected", item.key)}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
              );
            })
          ) : (
            <div style={{ border: "1px solid #edf0f2", borderRadius: 8, padding: 12 }}>No media submitted</div>
          )}
        </div>
      </div>

      {!readOnlyActions && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "flex-end",
            justifyContent: "space-between",
            padding: 14,
            borderTop: "1px solid #edf0f2",
            background: "#fcfcfc",
          }}
        >
          <div style={{ flex: "1 1 360px" }}>
            <label className="my-1 text-dark" style={{ fontWeight: 600 }}>
              Remark
            </label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="Remark required while rejecting"
              value={remark || review?.remark || ""}
              onChange={(e) => setRemark((prev) => ({ ...prev, [assetType]: e.target.value }))}
            />
          </div>
          {!perItemReview && <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-success"
              style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}
              disabled={actionLoading || !isPending}
              onClick={() => onAssetAction(assetType, "approved")}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: 13, fontWeight: 500 }}
              disabled={actionLoading || !isPending}
              onClick={() => onAssetAction(assetType, "rejected")}
            >
              Reject
            </button>
          </div>}
        </div>
      )}
    </div>
  );
};

const Stage3ControlsPanel = ({
  controls = {},
  form,
  setForm,
  onFileChange,
  onSave,
  actionLoading,
  readOnly,
}) => (
  <div style={{ border: "1px solid #d7dce1", borderRadius: 8, marginBottom: 18, background: "#fff" }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        background: "#f8f9fa",
        borderBottom: "1px solid #d7dce1",
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 18, color: "#2f343a" }}>Stage 3 Controls</div>
        <div style={{ color: "#343a40", fontSize: 14 }}>
          Agreement and induction status
        </div>
      </div>
      {controls.agreementFile ? renderDocumentLink(controls.agreementFile, "Agreement") : null}
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, padding: 14 }}>
      <div className="form-group">
        <label className="my-1 text-dark" style={{ fontWeight: 600 }}>
          Agreement Sign
        </label>
        <select
          className="form-control"
          value={form.agreementStatus || "pending"}
          onChange={(e) => setForm((prev) => ({ ...prev, agreementStatus: e.target.value }))}
          disabled={readOnly}
        >
          <option value="done">Done</option>
          <option value="pending">Pending</option>
          <option value="declined">Declined</option>
        </select>
      </div>
      <div className="form-group">
        <Input
          type="file"
          className="form-control"
          label="Agreement Upload"
          onChange={onFileChange}
          disabled={readOnly}
        />
      </div>
      <div className="form-group">
        <label className="my-1 text-dark" style={{ fontWeight: 600 }}>
          Induction
        </label>
        <select
          className="form-control"
          value={form.inductionStatus || "not_done"}
          onChange={(e) => setForm((prev) => ({ ...prev, inductionStatus: e.target.value }))}
          disabled={readOnly}
        >
          <option value="done">Done</option>
          <option value="not_done">Not Done</option>
        </select>
      </div>
      {!readOnly && (
      <div className="d-flex align-items-end">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          style={{ fontSize: 13, fontWeight: 500, minWidth: 110 }}
          disabled={actionLoading}
          onClick={onSave}
        >
          Save Stage 3
        </button>
      </div>
      )}
    </div>
  </div>
);

const onboardingDisplayLabel = (value, options = onboardingYesNoOptions) => {
  const option = options.find((item) => item.value === value);
  return option?.label || formatValue(value);
};

const AgreementInductionPanel = ({ details = {} }) => {
  const controls = details.stage3Controls || {};
  const onboardingControls = details.onboardingControls || {};
  const agreementStatus =
    onboardingControls.agreementSignStatus ||
    (controls.agreementStatus === "done" ? "yes" : controls.agreementStatus === "declined" ? "no" : "pending");
  const inductionStatus =
    onboardingControls.inductionStatus ||
    (controls.inductionStatus === "done" ? "yes" : "pending");
  const agreementUploaded =
    onboardingControls.agreementUpload ||
    (controls.agreementFile ? "yes" : "pending");
  const agreementFields = [
    { label: "Agreement Date", value: onboardingControls.agreementDate },
    { label: "Agreement Sign", value: onboardingDisplayLabel(agreementStatus) },
    {
      label: "Agreement Upload",
      value: controls.agreementFile
        ? renderDocumentLink(controls.agreementFile, "Agreement")
        : onboardingDisplayLabel(agreementUploaded),
    },
    { label: "Induction Date", value: onboardingControls.inductionDate },
    { label: "Induction", value: onboardingDisplayLabel(inductionStatus) },
  ];

  return (
    <div style={{ border: "1px solid #d7dce1", borderRadius: 8, marginBottom: 18, background: "#fff" }}>
      <div
        style={{
          padding: "12px 14px",
          background: "#f8f9fa",
          borderBottom: "1px solid #d7dce1",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 18, color: "#2f343a" }}>Agreement & Induction</div>
        <div style={{ color: "#343a40", fontSize: 14 }}>
          Agreement file and induction status uploaded from onboarding
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10, padding: 14 }}>
        {agreementFields.map((field) => (
          <div key={field.label} style={{ border: "1px solid #edf0f2", borderRadius: 8, padding: 12, minHeight: 82 }}>
            <div style={{ color: "#050505", fontWeight: 600, marginBottom: 8 }}>{field.label}</div>
            <div style={{ color: "#343a40", wordBreak: "break-word" }}>{field.value || "---"}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const stage3StatusOptions = [
  { value: "active", label: "Active", description: "Live consultants" },
  { value: "block", label: "Block", description: "Toggle off IDs" },
  { value: "deleted", label: "Deleted", description: "Deleted ID bin" },
  { value: "request", label: "Request", description: "Consultant requests" },
];

const consultantDataStatusOptions = stage3StatusOptions.filter(
  (option) => option.value !== "request"
);

const Stage3StatusSelector = ({ value, onChange, options = stage3StatusOptions }) => (
  <div style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
    {options.map((option) => {
      const active = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          style={{
            minWidth: 155,
            height: 40,
            border: "1px solid #ff7f7f",
            borderRadius: 4,
            background: active ? "#ff7f7f" : "#fff",
            color: active ? "#fff" : "#ff6b6b",
            fontWeight: active ? 700 : 500,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const ConsultantStatusControls = ({
  consultant,
  consultantId,
  actionLoading,
  onStatusToggle,
  onFakeToggle,
  allowFakeToggle = true,
  statusActivationLocked = false,
}) => {
  if (!consultantId) return null;
  const isActive = !statusActivationLocked && consultant.status === true;

  return (
    <div
      style={{
        border: "1px solid #d7dce1",
        borderRadius: 8,
        padding: "12px 14px",
        marginBottom: 18,
        background: "#fff",
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 10 }}>Consultant Status Controls</div>
      <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Switch
            checked={isActive}
            disabled={statusActivationLocked || actionLoading}
            onChange={() => !actionLoading && onStatusToggle(consultantId, isActive)}
          />
          <div>
            <div style={{ fontWeight: 700 }}>Status</div>
            <div style={{ color: "#6c757d", fontSize: 12 }}>
              {isActive ? "Active" : statusActivationLocked ? "Inactive until Final Status is Live" : "Blocked"}
            </div>
          </div>
        </div>
        {/* {allowFakeToggle && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Switch
              checked={!!consultant.isFake}
              onChange={() => !actionLoading && onFakeToggle(consultantId, !!consultant.isFake)}
            />
            <div>
              <div style={{ fontWeight: 700 }}>is fake ?</div>
              <div style={{ color: "#6c757d", fontSize: 12 }}>
                {consultant.isFake ? "Fake" : "Real"}
              </div>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

const displaySubmittedValue = (value) => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.join(", ");
    } catch (_) {}
  }
  return formatValue(value);
};

const InfoGrid = ({ title, fields }) => (
  <div style={{ marginTop: 14 }}>
    <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 18, color: "#2f343a" }}>{title}</div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
        border: "1px solid #dee2e6",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {fields.map(({ label, value }) => (
        <div
          key={label}
          style={{
            minHeight: 66,
            padding: "11px 12px",
            borderRight: "1px solid #edf0f2",
            borderBottom: "1px solid #edf0f2",
            background: "#fff",
          }}
        >
          <div style={{ fontSize: 14, color: "#050505", fontWeight: 600, marginBottom: 5 }}>
            {label}
          </div>
          <div style={{ wordBreak: "break-word", color: "#111827", fontSize: 14, lineHeight: 1.45 }}>
            {displaySubmittedValue(value)}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const analyticsSectionConfig = {
  interview: {
    title: "Interview Analytics",
    cards: (analytics) => [
      { label: "Interview Candidates", value: analytics.totalInterviewCandidates || 0 },
      { label: "Interview Done", value: analytics.interviewCompleted || 0 },
      { label: "Interview Pending", value: analytics.interviewPending || 0 },
      { label: "Interview Selected", value: analytics.selected || 0 },
      { label: "Interview Rejected", value: analytics.rejected || 0 },
    ],
  },
  onboarding: {
    title: "On-boarding Analytics",
    cards: (analytics) => [
      { label: "Form Submitted", value: analytics.formSubmitted || 0 },
      { label: "Form Not Submitted", value: analytics.formNotSubmitted || 0 },
      { label: "Selection Mail Sent", value: analytics.selectionMailSent || 0 },
      { label: "Selection Call Done", value: analytics.selectionCallDone || 0 },
      { label: "Photos Approved", value: analytics.photosApproved || 0 },
      { label: "PAN Approved", value: analytics.panApproved || 0 },
      { label: "Bank Approved", value: analytics.bankApproved || 0 },
      { label: "Agreement Signed", value: analytics.agreementSigned || 0 },
      { label: "Agreement Uploaded", value: analytics.agreementUploaded || 0 },
      { label: "Induction Done", value: analytics.inductionDone || 0 },
      { label: "Final Live", value: analytics.finalLive || 0 },
    ],
  },
  consultantData: {
    title: "Consultant Data Analytics",
    cards: (analytics) => [
      { label: "Final Profiles", value: analytics.approved || analytics.finalLive || 0 },
      { label: "Profile Complete", value: analytics.profileComplete || analytics.approved || 0 },
      { label: "Photos Approved", value: analytics.photosApproved || 0 },
      { label: "PAN Approved", value: analytics.panApproved || 0 },
      { label: "Bank Approved", value: analytics.bankApproved || 0 },
      { label: "Live Consultants", value: analytics.finalLive || 0 },
    ],
  },
};

const InterviewAnalyticsReport = ({ analytics = {}, section = "interview" }) => {
  const config = analyticsSectionConfig[section] || analyticsSectionConfig.interview;
  const cards = config.cards(analytics);

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontWeight: 700, fontSize: 18, color: "#2f343a", marginBottom: 10 }}>
        {config.title}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 10,
        }}
      >
        {cards.map((card) => (
          <div
            key={card.label}
            style={{
              border: "1px solid #d7dce1",
              borderRadius: 8,
              background: "#fff",
              padding: "12px 14px",
              minHeight: 82,
            }}
          >
            <div style={{ color: "#343a40", fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
              {card.label}
            </div>
            <div style={{ color: "#111827", fontSize: 24, fontWeight: 600, marginTop: 6 }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SubmittedFormDetailsModal = ({
  details,
  onClose,
  stage3Mode,
  stage3Status,
  onRequestAction,
  onConsultantAction,
  onConsultantBin,
  onConsultantRestore,
  onStatusToggle,
  onFakeToggle,
  onAssetAction,
  onRequestCorrectionMail,
  onAssetCorrectionMail,
  onOpenReview,
  onViewConsultant,
  onEditConsultant,
  onEditConsultantV1,
  canEditFormData,
  readOnlyConsultantReview,
  formDataEditMode,
  setFormDataEditMode,
  formDataEdit,
  setFormDataEdit,
  documentEditFiles,
  setDocumentEditFiles,
  onFormDataSave,
  requestRemark,
  setRequestRemark,
  assetRemark,
  setAssetRemark,
  consultantRemark,
  setConsultantRemark,
  actionLoading,
}) => {
  const [showCorrectionHistory, setShowCorrectionHistory] = useState(false);
  if (!details) return null;
  const formData = buildDisplayFormData(details);
  const documents = buildDisplayDocuments(details);
  const consultant = details.consultantId || {};
  const requests = details.requests || [];
  const personalStats = getCompletionStats(details.formData || {}, requiredPersonalFields);
  const documentStats = getCompletionStats(documents, requiredDocumentFields);
  const consultantId = getConsultantRecordId(details);
  const stage3Stats = getStage3MandatoryStats(details);
  const consultantStatus = consultant?.isAdminVerify
    ? "Approved"
    : consultant?.isBin
    ? "Bin"
    : consultant?.disapprovedAt
    ? "Rejected"
    : "Pending";
  const personalOverviewFields = [
    { label: "Email", value: consultant.email || consultant.details?.email || formData.email },
    { label: "Date Of Birth", value: consultant.dateOfBirth || formData.dateOfBirth },
    { label: "Profile Bio", value: consultant.profileBio || consultant.bio || formData.profileBio },
    { label: "Secondary Number", value: consultant.secondaryMobile || consultant.details?.secondaryMobile || formData.secondaryNumber },
    { label: "Address", value: consultant.address || consultant.details?.address || formData.address },
    { label: "City", value: consultant.city || consultant.details?.city || formData.city },
    { label: "State", value: consultant.state || consultant.details?.state || formData.state },
    { label: "Pincode", value: consultant.pincode || consultant.details?.zip || formData.pincode },
    { label: "Place Of Birth", value: consultant.placeOfBirth || formData.placeOfBirth },
    { label: "Zodiac Sign", value: consultant.zodiacSign || formData.zodiacSign },
  ];
  const professionalOverviewFields = [
    { label: "Highest Educational Qualification", value: consultant.highestEducationalQualification || formData.highestEducationalQualification },
    { label: "Current Profession", value: consultant.currentProfession || formData.currentProfession },
    { label: "Primary Skill", value: consultant.primarySkill || formData.primarySkill },
    { label: "Other Skills", value: consultant.otherSkill || consultant.otherSkills || formData.otherSkill || formData.otherSkills },
    { label: "Learning Source", value: consultant.learningSource || formData.learningSource },
    { label: "Astrology Learning Source", value: consultant.astrologyLearningSource || formData.astrologyLearningSource },
    { label: "Experience In Years", value: consultant.experienceYears || consultant.experienceInYears || formData.experienceYears || formData.experienceInYears },
    { label: "Daily Work Hours", value: consultant.dailyWorkHours || formData.dailyWorkHours },
    { label: "Expertise", value: consultant.expertise || formData.expertise },
    { label: "Preferred Languages", value: consultant.preferLanguage || consultant.preferredLanguages || formData.preferLanguage || formData.preferredLanguages },
    { label: "Comfortable Topics", value: formatTopicNames(consultant.topics || formData.topics) },
    { label: "Selected Highest Qualification", value: consultant.highestQualification || consultant.selectedHighestQualification || formData.highestQualification || formData.selectedHighestQualification },
    { label: "Minimum Earning Expectation", value: consultant.minimumEarningExpectation || formData.minimumEarningExpectation },
  ];
  const additionalOverviewFields = [
    { label: "Heard About Vedic Meet", value: consultant.heardAboutVedicMeet || formData.heardAboutVedicMeet },
    { label: "Works On Other Platform", value: consultant.otherOnlinePlatformWork || consultant.worksOnOtherPlatform || formData.otherOnlinePlatformWork || formData.worksOnOtherPlatform },
    { label: "Other Platform/App Name", value: consultant.otherOnlinePlatformName || consultant.onlinePlatformName || formData.otherOnlinePlatformName || formData.onlinePlatformName },
    { label: "Favourite Song", value: consultant.favouriteSong || formData.favouriteSong },
    { label: "Movie Genre", value: consultant.movieGenre || formData.movieGenre },
    { label: "Hobbies", value: consultant.hobbies || formData.hobbies },
    { label: "Referred To Vedic Meet", value: consultant.referredToVedicMeet || formData.referredToVedicMeet },
    { label: "Currently Working Full Time", value: consultant.fullTimeJob || consultant.currentlyWorkingFullTime || formData.fullTimeJob || formData.currentlyWorkingFullTime },
  ];
  const assetReviews = details.assetReviews || {};
  const photoReviewItems = Array.isArray(assetReviews.photos?.items) ? assetReviews.photos.items : [];
  const photoReview = (key) => photoReviewItems.find((item) => item.key === key);
  const photoItems = [
    documents.profileImage ? { key: "profileImage", label: "Profile Photo", url: documents.profileImage, review: photoReview("profileImage") } : null,
    ...(Array.isArray(documents.galleryPhotos)
      ? documents.galleryPhotos.map((url, index) => ({ key: `galleryPhotos.${index}`, label: `Gallery Photo ${index + 1}`, url, review: photoReview(`galleryPhotos.${index}`) }))
      : []),
  ].filter(Boolean);
  const videoItems = documents.videoIntroduction
    ? [{ label: "Introduction Video", url: documents.videoIntroduction }]
    : [];
  const visibleRequests = stage3Mode && !["request", "block"].includes(stage3Status)
    ? requests.filter((request) => Number(request.approveStatus) === 2)
    : requests;
  const isRequestMode = stage3Mode && stage3Status === "request";
  const isBlockMode = stage3Mode && stage3Status === "block";
  const isDeletedMode = stage3Mode && stage3Status === "deleted";
  const stage3ReadOnlyActions = stage3Mode && !isRequestMode;
  const canAddReview = isRequestMode;
  const canShowStatusControls = isRequestMode || isBlockMode;
  const canShowConsultantProfileButton = consultantId && !isBlockMode;
  const canShowConsultantEditButtons = isRequestMode && consultantId;
  const canShowReviews = isRequestMode && consultantId;

  return (
    <ResponsiveDetailPage
      title={stage3Mode ? stage3DetailTitles[stage3Status] || "Stage 3 Consultant Details" : "On-boarding Details"}
      onBack={onClose}
    >
      <div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "stretch",
            marginBottom: 18,
          }}
        >
          <div
            style={{
              flex: "1 1 100%",
              border: "1px solid #d7dce1",
              borderRadius: 8,
              padding: 14,
              background: "#fff",
            }}
          >
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 14 }}>
              {consultant.profileImage ? (
                <img
                  src={consultant.profileImage}
                  alt="Consultant"
                  style={{ width: 58, height: 58, borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                <div
                  style={{
                    width: 58,
                    height: 58,
                    borderRadius: "50%",
                    background: "#f1f3f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  {(formData.name || "C").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <div style={{ fontWeight: 800, fontSize: 18 }}>
                  {consultant.accountName || consultant.name || formData.name || "---"}
                </div>
                <div style={{ color: "#6c757d" }}>
                  {consultant.userName || consultant.username || formData.userName || formData.username || "---"} | {consultant.userId || consultantId || "---"}
                </div>
              </div>
            </div>
            <div className="content-table">
              <table>
                <tbody>
                  <tr>
                    <th>Email</th>
                    <td>{formData.email || consultant.email || consultant.details?.email || "---"}</td>
                    <th>Date Of Birth</th>
                    <td>{formData.dateOfBirth || consultant.dateOfBirth || "---"}</td>
                  </tr>
                  <tr>
                    <th>Category</th>
                    <td>{details.preSelectedConsultantId?.category || formData.category || "---"}</td>
                    <th>Source</th>
                    <td>{details.preSelectedConsultantId?.source || formData.source || "---"}</td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td>{consultant.mobile || consultant.details?.phone || formData.primaryNumber || "---"}</td>
                    <th>Type</th>
                    <td>Consultant</td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td>{consultant.gender || consultant.details?.gender || formData.gender || "---"}</td>
                    <th>Status</th>
                    <td>{consultantStatus}</td>
                  </tr>
                  <tr>
                    <th>Registration Date</th>
                    <td>{formatDateTime(consultant.createdAt || consultant.created_at || details.createdAt)}</td>
                    <th>Status Date</th>
                    <td>{formatDateTime(consultant.approvalActionAt || consultant.approvedAt || consultant.disapprovedAt)}</td>
                  </tr>
                  <tr>
                    <th>Live Date</th>
                    <td>{formatDateTime(getStage3FieldValue("liveDate", details))}</td>
                    <th>Price Source</th>
                    <td>{getStage3FieldValue("priceSource", details) || "---"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {stage3Stats.missing.length > 0 && (
              <div style={{ marginTop: 10, color: "#dc3545", fontSize: 13 }}>
                Missing mandatory fields: {stage3Stats.missing.map((field) => humanizeKey(field)).join(", ")}
              </div>
            )}
            {(canShowConsultantProfileButton || canShowConsultantEditButtons) && (
              <div className="d-flex justify-content-end gap-2 mt-3">
                {/* {canShowConsultantProfileButton && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: 13, fontWeight: 500 }}
                    onClick={() => onViewConsultant(consultantId)}
                  >
                    View Profile
                  </button>
                )} */}
                {canShowConsultantEditButtons && (
                  <>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  style={{ fontSize: 13, fontWeight: 500 }}
                  onClick={() => onEditConsultant(consultantId)}
                >
                  Edit Consultant
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}
                  onClick={() => onEditConsultantV1(consultantId)}
                >
                  Edit Consultant V1
                </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div
            style={{
              flex: "1 1 520px",
              border: "1px solid #d7dce1",
              borderRadius: 8,
              padding: 14,
              background: "#fff",
            }}
          >
            <InfoGrid title="Personal Details" fields={[...personalOverviewFields, ...additionalOverviewFields]} />
          </div>

          <div
            style={{
              flex: "1 1 520px",
              border: "1px solid #d7dce1",
              borderRadius: 8,
              padding: 14,
              background: "#fff",
            }}
          >
            <InfoGrid title="Professional Details" fields={professionalOverviewFields} />
          </div>

          <div
            style={{
              flex: "1 1 100%",
              border: "1px solid #d7dce1",
              borderRadius: 8,
              padding: 14,
              background: "#fff",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 18, color: "#2f343a" }}>
              {canEditFormData ? "Consultant Request Details" : "Consultant Request Review"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div style={{ border: "1px solid #edf0f2", borderRadius: 8, padding: 12 }}>
                <div style={{ color: "#343a40", fontSize: 15, fontWeight: 500 }}>Personal Details</div>
                <div style={{ fontWeight: 500, fontSize: 18 }}>{personalStats.percent}%</div>
                <div>{personalStats.completed} / {personalStats.total} submitted</div>
              </div>
              <div style={{ border: "1px solid #edf0f2", borderRadius: 8, padding: 12 }}>
                <div style={{ color: "#343a40", fontSize: 15, fontWeight: 500 }}>Documents</div>
                <div style={{ fontWeight: 500, fontSize: 18 }}>{documentStats.percent}%</div>
                <div>{documentStats.completed} / {documentStats.total} submitted</div>
                {documentStats.missing.length > 0 && (
                  <div style={{ marginTop: 8, color: "#dc3545", fontSize: 13, lineHeight: 1.4 }}>
                    Missing: {documentStats.missing.map((field) => requiredDocumentLabels[field] || humanizeKey(field)).join(", ")}
                  </div>
                )}
              </div>
              <div style={{ border: "1px solid #edf0f2", borderRadius: 8, padding: 12 }}>
                <div style={{ color: "#343a40", fontSize: 15, fontWeight: 500 }}>Stage 3 Mandatory</div>
                <div style={{ fontWeight: 500, fontSize: 18 }}>{stage3Stats.percent}%</div>
                <div>{stage3Stats.completed} / {stage3Stats.total} filled</div>
              </div>
            </div>
            {!readOnlyConsultantReview && (
              <>
                <label className="my-1 text-dark" style={{ fontWeight: 600 }}>
                  Consultant Remark
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Remark required while rejecting consultant"
                  value={consultantRemark}
                  onChange={(e) => setConsultantRemark(e.target.value)}
                />
              </>
            )}
            {consultantId && !readOnlyConsultantReview && (
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}
                  disabled={actionLoading || consultant.isAdminVerify}
                  onClick={() => onConsultantAction(consultantId, true)}
                >
                  {consultant.isAdminVerify ? "Approved" : "Approve Consultant"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ fontSize: 13, fontWeight: 500 }}
                  disabled={actionLoading}
                  onClick={() => onConsultantAction(consultantId, false)}
                >
                  Reject Consultant
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{ fontSize: 13, fontWeight: 500 }}
                  disabled={actionLoading}
                  onClick={() => onConsultantBin(consultantId)}
                >
                  Bin
                </button>
              </div>
            )}
          </div>
        </div>

        <h5 className="mb-3">Bank & PAN Requests</h5>
        {visibleRequests.length ? (
          visibleRequests.map((request) => (
            <RequestReviewPanel
              key={request._id}
              request={request}
              requestRemark={requestRemark}
              setRequestRemark={setRequestRemark}
              onRequestAction={onRequestAction}
              onCorrectionMail={onRequestCorrectionMail}
              actionLoading={actionLoading}
              readOnlyActions={stage3ReadOnlyActions}
            />
          ))
        ) : (
          <div style={{ border: "1px solid #d7dce1", borderRadius: 8, padding: 14, marginBottom: 18 }}>
            {stage3Mode && !isRequestMode ? "No approved bank or PAN request found" : "No bank or PAN request found"}
          </div>
        )}
        <div className="d-flex justify-content-end mb-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: 13, fontWeight: 500 }}
            onClick={() => setShowCorrectionHistory(true)}
          >
            Correction History ({(details.correctionLogs || []).length})
          </button>
        </div>
        {showCorrectionHistory && (
          <CorrectionHistoryModal
            logs={details.correctionLogs || []}
            onClose={() => setShowCorrectionHistory(false)}
          />
        )}

        <AgreementInductionPanel details={details} />

        <h5 className="mb-3">Photo & Video Management</h5>
        <MediaReviewPanel
          assetType="photos"
          title="Photos"
          submitted={photoItems.length}
          required={1}
          items={photoItems}
          review={assetReviews.photos}
          remark={assetRemark.photos}
          setRemark={setAssetRemark}
          onAssetAction={onAssetAction}
          onCorrectionMail={onAssetCorrectionMail}
          actionLoading={actionLoading}
          readOnlyActions={stage3ReadOnlyActions}
          perItemReview
        />
        <MediaReviewPanel
          assetType="video"
          title="Video"
          submitted={videoItems.length}
          required={1}
          items={videoItems}
          review={assetReviews.video}
          remark={assetRemark.video}
          setRemark={setAssetRemark}
          onAssetAction={onAssetAction}
          onCorrectionMail={onAssetCorrectionMail}
          actionLoading={actionLoading}
          readOnlyActions={stage3ReadOnlyActions}
        />

        {consultantId && isDeletedMode && (
          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              style={{ fontSize: 13, fontWeight: 500 }}
              disabled={actionLoading}
              onClick={() => onConsultantRestore(consultantId)}
            >
              Undelete
            </button>
          </div>
        )}

        {consultantId && canAddReview && (
          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              style={{ fontSize: 13, fontWeight: 500 }}
              onClick={() => onOpenReview(consultantId)}
            >
              Add Review ({details.reviewCount || 0})
            </button>
          </div>
        )}

        {canShowStatusControls && (
          <ConsultantStatusControls
            consultant={consultant}
            consultantId={consultantId}
            actionLoading={actionLoading}
            onStatusToggle={onStatusToggle}
            onFakeToggle={onFakeToggle}
            allowFakeToggle={!isBlockMode}
            statusActivationLocked={isRequestMode}
          />
        )}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Form Data</h5>
          <div className="d-flex gap-2">
            {canEditFormData && (
              formDataEditMode ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    style={{ color: "#fff" }}
                    onClick={onFormDataSave}
                    disabled={actionLoading}
                  >
                    Save Details
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setFormDataEditMode(false);
                      setFormDataEdit(buildConsultantEditData(details));
                      setDocumentEditFiles({});
                    }}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                    setFormDataEdit(buildConsultantEditData(details));
                    setFormDataEditMode(true);
                  }}
                >
                  Edit Details
                </button>
              )
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => downloadSubmittedForm(details)}
            >
              Download Form
            </button>
          </div>
        </div>
        <div className="content-table mb-4">
          <table>
            <tbody>
              {Object.keys(formData).length > 0 ? (
                Object.entries(formData).map(([key, value]) => (
                  <tr key={key}>
                    <th style={{ width: "260px", textTransform: "capitalize" }}>
                      {humanizeKey(key)}
                    </th>
                    <td>
                      {formDataEditMode && !hiddenSubmittedFormFields.has(key) && !lockedConsultantEditFields.has(key) ? (
                        <input
                          type="text"
                          className="form-control"
                          value={formatValue(formDataEdit[key] ?? "") === "---" ? "" : formatValue(formDataEdit[key] ?? "")}
                          onChange={(e) =>
                            setFormDataEdit((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                        />
                      ) : (
                        formatValue(value)
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>No form data found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <h5 className="mb-3">Documents</h5>
        <div className="content-table">
          <table>
            <tbody>
              {Object.keys(documents).length > 0 ? (
                Object.entries(documents).map(([key, value]) => (
                  <tr key={key}>
                    <th style={{ width: "260px", textTransform: "capitalize" }}>
                      {humanizeKey(key)}
                    </th>
                    <td>
                      {Array.isArray(value) ? (
                        value.length ? (
                          value.map((url, index) => (
                            <span key={url} className="me-3">
                              <MediaPreview
                                url={url}
                                type={
                                  key === "videoIntroduction"
                                    ? "video"
                                  : key === "galleryPhotos" || key === "profileImage" || key === "certificateOfCompletion"
                                    ? "image"
                                    : undefined
                                }
                                alt={`${humanizeKey(key)} ${index + 1}`}
                              />
                              <a href={url} target="_blank" rel="noreferrer" className="me-2">
                                View {index + 1}
                              </a>
                              <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={() => downloadFile(url, `${key}-${index + 1}`)}
                              >
                                Download {index + 1}
                              </button>
                            </span>
                          ))
                        ) : (
                          "---"
                        )
                      ) : value ? (
                        <>
                          <MediaPreview
                            url={value}
                            type={
                              key === "videoIntroduction"
                                ? "video"
                                : key === "galleryPhotos" || key === "profileImage" || key === "certificateOfCompletion"
                                ? "image"
                                : undefined
                            }
                            alt={humanizeKey(key)}
                          />
                          <a href={value} target="_blank" rel="noreferrer" className="me-2">
                            View
                          </a>
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => downloadFile(value, key)}
                          >
                            Download
                          </button>
                        </>
                      ) : (
                        "---"
                      )}
                      {canEditFormData && formDataEditMode && editableDocumentFields.includes(key) && (
                        <div style={{ marginTop: 8 }}>
                          <Input
                            type="file"
                            className="form-control"
                            multiple={key === "galleryPhotos"}
                            onChange={(e) =>
                              setDocumentEditFiles((prev) => ({
                                ...prev,
                                [key]: key === "galleryPhotos" ? Array.from(e.target.files || []) : e.target.files?.[0],
                              }))
                            }
                          />
                          {documentEditFiles[key] ? (
                            <div style={{ color: "#6c757d", fontSize: 12, marginTop: 4 }}>
                              {Array.isArray(documentEditFiles[key])
                                ? `${documentEditFiles[key].length} file selected`
                                : documentEditFiles[key].name}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>No documents found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {canShowReviews && (
          <div style={{ marginTop: 20 }}>
            <h5 className="mb-3">Reviews</h5>
            <ConsultantReviewsListing consultantId={consultantId} />
          </div>
        )}
      </div>
    </ResponsiveDetailPage>
  );
};

const EmailTemplateEditor = ({
  templates,
  activeTemplateType,
  setActiveTemplateType,
  onChange,
  onSave,
  saving,
  buttonref,
}) => {
  const editorRef = useRef(null);
  const activeTemplate = templates.find((item) => item.type === activeTemplateType) || {};
  const placeholders = templatePlaceholders[activeTemplateType] || [];
  const editorConfig = useMemo(
    () => richTextEditorConfig,
    []
  );

  const insertPlaceholder = (placeholder) => {
    const currentBody = activeTemplate.body || "";
    const editor = editorRef.current;
    if (editor?.selection?.insertHTML) {
      editor.selection.insertHTML(` ${placeholder} `);
      onChange(activeTemplateType, "body", editor.value || currentBody);
      return;
    }
    onChange(activeTemplateType, "body", `${currentBody} ${placeholder}`);
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {Object.keys(templateLabels).map((type) => (
          <button
            key={type}
            type="button"
            className={`btn btn-sm ${activeTemplateType === type ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setActiveTemplateType(type)}
          >
            {templateLabels[type]}
          </button>
        ))}
      </div>

      <div className="row">
        <div className="col-md-7">
          <div className="form-group mb-3">
            <Input
              type="text"
              value={activeTemplate.subject || ""}
              onChange={(e) => onChange(activeTemplateType, "subject", e.target.value)}
              className="form-control"
              label="Email Subject"
              required
              placeholder="Email subject"
            />
          </div>
          <div className="form-group mb-3">
            <label className="my-1 text-dark">
              Email Body <span className="text-danger">*</span>
            </label>
            <div id="myjoditEditor">
              <JoditEditor
                ref={editorRef}
                value={activeTemplate.body || ""}
                tabIndex={1}
                onBlur={(value) => onChange(activeTemplateType, "body", value)}
                config={editorConfig}
              />
            </div>
          </div>
          <div className="form-group mb-3">
            <label className="my-1 text-dark">Insert Detail</label>
            <div className="d-flex flex-wrap gap-2">
              {placeholders.map((placeholder) => (
                <button
                  key={placeholder.value}
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => insertPlaceholder(placeholder.value)}
                >
                  {placeholder.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="btn_size"
            ref={buttonref}
            onClick={() => onSave(activeTemplateType)}
            disabled={saving}
          >
            Save Template
          </button>
        </div>
        <div className="col-md-5">
          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>Dynamic Detail</th>
                </tr>
              </thead>
              <tbody>
                {placeholders.map((placeholder) => (
                  <tr key={placeholder.value}>
                    <td>
                      {placeholder.label}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <h6>Preview</h6>
            <div
              className="border rounded p-3 bg-white"
              style={{ minHeight: "220px", lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: activeTemplate.body || "" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const PersonalizedEmailModal = ({
  show,
  consultant,
  form,
  error,
  onClose,
  onChange,
  onConfirm,
}) => {
  const editorRef = useRef(null);
  const editorConfig = useMemo(() => richTextEditorConfig, []);

  const insertPlaceholder = (placeholder) => {
    const currentBody = form.body || "";
    const editor = editorRef.current;
    if (editor?.selection?.insertHTML) {
      editor.selection.insertHTML(` ${placeholder} `);
      onChange("body", editor.value || currentBody);
      return;
    }
    onChange("body", `${currentBody} ${placeholder}`);
  };
  const previewVariables = buildPersonalMailPreviewVariables(consultant);
  const previewSubject = renderPreviewTemplate(form.subject || "", previewVariables);
  const previewBody = renderPreviewTemplate(form.body || "", previewVariables);

  return (
    <ModalBox
      status={show}
      onClose={onClose}
      title={`Send Personal Mail${consultant?.name ? ` - ${consultant.name}` : ""}`}
      size="lg"
      closeIcon
      custom
      customizedFooter={
        <div className="model_custom_footer">
          <button className="btn_size" onClick={onConfirm}>
            Send Mail
          </button>
        </div>
      }
    >
      <div>
        <div className="form-group mb-3">
          <Input
            type="text"
            value={form.subject || ""}
            onChange={(e) => onChange("subject", e.target.value)}
            className="form-control"
            label="Email Subject"
            required
            placeholder="Email subject"
            error={error.subject}
          />
        </div>
        <div className="form-group mb-3">
          <label className="my-1 text-dark">
            Email Body <span className="text-danger">*</span>
          </label>
          <div id="myjoditEditor">
            <JoditEditor
              ref={editorRef}
              value={form.body || ""}
              tabIndex={1}
              onBlur={(value) => onChange("body", value)}
              onChange={(value) => onChange("body", value)}
              config={editorConfig}
            />
          </div>
          {error.body && <div className="invalid-feedback d-block">{error.body}</div>}
        </div>
        <div className="form-group mb-2">
          <label className="my-1 text-dark">Insert Detail</label>
          <div className="d-flex flex-wrap gap-2">
            {personalMailPlaceholders.map((placeholder) => (
              <button
                key={placeholder.value}
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => insertPlaceholder(placeholder.value)}
              >
                {placeholder.label}
              </button>
            ))}
          </div>
        </div>
        <div className="form-group mt-4">
          <label className="my-1 text-dark">Mail Preview</label>
          <div className="border rounded bg-white">
            <div className="border-bottom p-3">
              <strong>Subject:</strong> {previewSubject || "---"}
            </div>
            <div
              className="p-3"
              style={{ minHeight: "180px", lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: previewBody || "---" }}
            />
          </div>
        </div>
      </div>
    </ModalBox>
  );
};

const InterviewRescheduleModal = ({ show, consultant, form, error, onClose, onChange, onSave }) => (
  <ModalBox
    status={show}
    onClose={onClose}
    title={`Reschedule Interview Mail${consultant?.name ? ` - ${consultant.name}` : ""}`}
    size="sm"
    closeIcon
    custom
    customizedFooter={
      <div className="model_custom_footer">
        <button className="btn_size" onClick={onSave}>
          Queue Mail
        </button>
      </div>
    }
  >
    <div>
      <div className="form-group mb-2">
        <label className="my-1 text-dark">
          Interview Date & Time <span className="text-danger">*</span>
        </label>
        <ReactDatePicker
          selected={form.interviewDateTime}
          onChange={(date) => onChange("interviewDateTime", date)}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={5}
          dateFormat="yyyy-MM-dd h:mm aa"
          className={`form-control ${error.interviewDateTime ? "is-invalid" : ""}`}
          placeholderText="Select interview date & time"
        />
        {error.interviewDateTime && (
          <div className="invalid-feedback d-block">{error.interviewDateTime}</div>
        )}
      </div>
      <div className="form-group mb-2">
        <label className="my-1 text-dark">
          Send Mail At <span className="text-danger">*</span>
        </label>
        <ReactDatePicker
          selected={form.scheduleEmailAt}
          onChange={(date) => onChange("scheduleEmailAt", date)}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={5}
          dateFormat="yyyy-MM-dd h:mm aa"
          className={`form-control ${error.scheduleEmailAt ? "is-invalid" : ""}`}
          placeholderText="Select mail send date & time"
        />
        {error.scheduleEmailAt && (
          <div className="invalid-feedback d-block">{error.scheduleEmailAt}</div>
        )}
      </div>
      <div className="form-group mb-2">
        <TextArea
          value={form.remark || ""}
          onChange={(e) => onChange("remark", e.target.value)}
          className="form-control"
          label="Remark"
          placeholder="Reason for reschedule"
          rows={3}
        />
      </div>
    </div>
  </ModalBox>
);

const InterviewLogsModal = ({ show, consultant, onClose }) => {
  const logs = [...(consultant?.activityLogs || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <ModalBox
      status={show}
      onClose={onClose}
      title={`Activity Logs${consultant?.name ? ` - ${consultant.name}` : ""}`}
      size="lg"
      closeIcon
      custom
      customizedFooter={
        <div className="model_custom_footer">
          <button className="btn_size" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      <div style={{ maxHeight: 460, overflowY: "auto" }}>
        {logs.length ? (
          logs.map((log, index) => (
            <div
              key={`${log.createdAt}-${index}`}
              style={{
                border: "1px solid #edf0f4",
                borderRadius: 6,
                padding: 12,
                marginBottom: 10,
                background: "#fff",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <strong style={{ textTransform: "capitalize" }}>
                  {String(log.action || "activity").replace(/_/g, " ")}
                </strong>
                <span style={{ color: "#6c757d", whiteSpace: "nowrap" }}>
                  {formatDateTime(log.createdAt)}
                </span>
              </div>
              <div style={{ marginTop: 4 }}>{log.message || "---"}</div>
              {log.meta && Object.keys(log.meta).length ? (
                <pre
                  style={{
                    marginTop: 8,
                    marginBottom: 0,
                    padding: 8,
                    background: "#f8f9fa",
                    borderRadius: 4,
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {JSON.stringify(log.meta, null, 2)}
                </pre>
              ) : null}
            </div>
          ))
        ) : (
          <div className="text-center p-4">No activity logs found.</div>
        )}
      </div>
    </ModalBox>
  );
};

const SubmittedCommunicationModal = ({ show, selectedCount, form, error, onClose, onChange, onSend }) => (
  <ModalBox
    status={show}
    onClose={onClose}
    title={`Send Onboarding Communication (${selectedCount})`}
    size="lg"
    closeIcon
    custom
    customizedFooter={
      <div className="model_custom_footer">
        <button className="btn_size" onClick={onSend}>
          Send
        </button>
      </div>
    }
  >
    <div>
      <div className="form-group mb-3">
        <label className="my-1 text-dark">Channel</label>
        <select
          className="form-control"
          value={form.channel}
          onChange={(e) => onChange("channel", e.target.value)}
        >
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="both">Email + WhatsApp</option>
        </select>
      </div>
      {["email", "both"].includes(form.channel) && (
        <>
          <div className="form-group mb-3">
            <Input
              type="text"
              value={form.subject || ""}
              onChange={(e) => onChange("subject", e.target.value)}
              className="form-control"
              label="Email Subject"
              placeholder="Email subject"
              error={error.subject}
            />
          </div>
          <div className="form-group mb-3">
            <TextArea
              value={form.body || ""}
              onChange={(e) => onChange("body", e.target.value)}
              className="form-control"
              label="Email Body"
              placeholder="Use {{name}}, {{formLink}}, {{phone}}, {{email}}"
              rows={6}
              error={error.body}
            />
          </div>
        </>
      )}
      {["whatsapp", "both"].includes(form.channel) && (
        <div className="form-group mb-3">
          <Input
            type="text"
            value={form.campaignName || ""}
            onChange={(e) => onChange("campaignName", e.target.value)}
            className="form-control"
            label="AiSensy Campaign Name"
            placeholder="AiSensy campaign / flow name"
            error={error.campaignName}
          />
        </div>
      )}
    </div>
  </ModalBox>
);

const CorrectionMailModal = ({ show, form, error, onClose, onChange, onSend }) => (
  <ModalBox
    status={show}
    onClose={onClose}
    title="Send Correction Mail"
    size="lg"
    closeIcon
    custom
    customizedFooter={
      <div className="model_custom_footer">
        <button className="btn_size" onClick={onSend}>
          Send Mail
        </button>
      </div>
    }
  >
    <div>
      <div className="mb-2" style={{ fontWeight: 700 }}>
        Section: {form.sectionLabel || "---"}
      </div>
      <div className="form-group mb-3">
        <Input
          type="text"
          value={form.subject || ""}
          onChange={(e) => onChange("subject", e.target.value)}
          className="form-control"
          label="Subject"
          placeholder="Mail subject"
          error={error.subject}
        />
      </div>
      <div className="form-group mb-3">
        <TextArea
          value={form.remark || ""}
          onChange={(e) => onChange("remark", e.target.value)}
          className="form-control"
          label="Remark"
          placeholder="Explain what the consultant needs to correct"
          rows={3}
          error={error.remark}
        />
      </div>
      <div className="form-group mb-3">
        <TextArea
          value={form.body || ""}
          onChange={(e) => onChange("body", e.target.value)}
          className="form-control"
          label="Mail Body"
          placeholder="Use {{formLink}} in the body if needed"
          rows={5}
        />
      </div>
    </div>
  </ModalBox>
);

const PreSelectedConsultantModal = ({
  show,
  onClose,
  selectedData,
  fetchList,
  mode = "interview",
}) => {
  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState({});
  const [buttonref, buttonLoader] = useButtonLoader("Save");
  const isEdit = Boolean(selectedData?._id);

  useEffect(() => {
    if (selectedData?._id) {
      setForm({
        ...selectedData,
        interviewDateTime: selectedData.interviewDateTime
          ? new Date(selectedData.interviewDateTime)
          : null,
        scheduleEmailAt: selectedData.scheduleEmailAt
          ? new Date(selectedData.scheduleEmailAt)
          : null,
      });
    } else {
      setForm(defaultForm);
    }
    setError({});
  }, [selectedData, show]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error[key]) setError((prev) => ({ ...prev, [key]: "" }));
  };

  const validation = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.name?.trim()) errors.name = "Name is required *";
    if (!form.email?.trim()) errors.email = "Email is required *";
    else if (!emailRegex.test(form.email)) errors.email = "Enter valid email *";
    if (!form.phone?.trim()) errors.phone = "Phone number is required *";
    if (!form.category?.trim()) errors.category = "Category is required *";
    if (!form.source?.trim()) errors.source = "Source is required *";
    if (!form.interviewDateTime) errors.interviewDateTime = "Interview date & time is required *";
    if (!form.scheduleEmailAt) errors.scheduleEmailAt = "Schedule email time is required *";

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const saveHandler = async () => {
    if (!validation()) return;

    try {
      buttonLoader(true);
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        category: form.category,
        source: form.source,
        interviewDateTime: new Date(form.interviewDateTime).toISOString(),
        scheduleEmailAt: new Date(form.scheduleEmailAt).toISOString(),
      };

      const { data } = isEdit
        ? await updatePreSelectedConsultantApi({
            ...payload,
            preSelectedConsultantId: selectedData._id,
          })
        : await addPreSelectedConsultantApi(payload);

      if (data.code === 200) {
        toastMessage(data.message, "success", "preSelectedConsultant");
        fetchList();
        onClose();
      } else {
        toastMessage(data.message, "error", "preSelectedConsultant");
      }
    } catch (err) {
      console.log(err);
      toastMessage(
        err?.response?.data?.message || err?.message || "Something went wrong",
        "error",
        "preSelectedConsultant"
      );
    } finally {
      buttonLoader(false);
    }
  };

  return (
    <ModalBox
      status={show}
      onClose={onClose}
      title={
        isEdit
          ? "Edit Interview Consultant"
          : mode === "onboarding"
            ? "Add Consultant For Onboarding"
            : "Add Interview Consultant"
      }
      size="sm"
      closeIcon
      custom
      customizedFooter={
        <div className="model_custom_footer">
          <button className="btn_size" ref={buttonref} onClick={saveHandler}>
            Save
          </button>
        </div>
      }
    >
      <div>
        {mode === "onboarding" && !isEdit && (
          <div
            style={{
              background: "#fff7f7",
              border: "1px solid #ffd0d0",
              color: "#343a40",
              borderRadius: 6,
              padding: "9px 11px",
              fontWeight: 600,
              marginBottom: 12,
              lineHeight: 1.4,
            }}
          >
            onboarding form process 
          </div>
        )}
        <div className="form-group mb-2">
          <Input
            type="text"
            value={form.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            className="form-control"
            label="Name"
            required
            placeholder="Name"
            error={error.name}
          />
        </div>
        <div className="form-group mb-2">
          <Input
            type="email"
            value={form.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
            className="form-control"
            label="Email"
            required
            placeholder="Email"
            error={error.email}
          />
        </div>
        <div className="form-group mb-2">
          <Input
            type="text"
            value={form.phone || ""}
            onChange={(e) => handleChange("phone", e.target.value)}
            className="form-control"
            label="Phone Number"
            required
            placeholder="Phone Number"
            error={error.phone}
          />
        </div>
        <div className="form-group mb-2">
          <Input
            type="text"
            value={form.category || ""}
            onChange={(e) => handleChange("category", e.target.value)}
            className="form-control"
            label="Category"
            required
            placeholder="Category"
            error={error.category}
          />
        </div>
        <div className="form-group mb-2">
          <Input
            type="text"
            value={form.source || ""}
            onChange={(e) => handleChange("source", e.target.value)}
            className="form-control"
            label="Source"
            required
            placeholder="Source"
            error={error.source}
          />
        </div>
        <div className="form-group mb-2">
          <label className="my-1 text-dark">
            Interview Date & Time <span className="text-danger">*</span>
          </label>
          <ReactDatePicker
            selected={form.interviewDateTime}
            onChange={(date) => handleChange("interviewDateTime", date)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={5}
            dateFormat="yyyy-MM-dd h:mm aa"
            className={`form-control ${error.interviewDateTime ? "is-invalid" : ""}`}
            placeholderText="Select interview date & time"
          />
          {error.interviewDateTime && (
            <div className="invalid-feedback d-block">{error.interviewDateTime}</div>
          )}
        </div>
        <div className="form-group mb-2">
          <label className="my-1 text-dark">
            Schedule Email Time <span className="text-danger">*</span>
          </label>
          <ReactDatePicker
            selected={form.scheduleEmailAt}
            onChange={(date) => handleChange("scheduleEmailAt", date)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={5}
            dateFormat="yyyy-MM-dd h:mm aa"
            className={`form-control ${error.scheduleEmailAt ? "is-invalid" : ""}`}
            placeholderText="Select email send date & time"
          />
          {error.scheduleEmailAt && (
            <div className="invalid-feedback d-block">{error.scheduleEmailAt}</div>
          )}
        </div>
      </div>
    </ModalBox>
  );
};

const PreSelectedConsultant = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingSubmittedOpenRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [search, setSearch] = useState("");
  const debounceValue = useDebounce(search || "", 300);
  const [listFilters, setListFilters] = useState({
    status: "",
    emailStatus: "",
    selectionMailStatus: "",
    interviewConduct: "",
    startDate: null,
    endDate: null,
  });
  const [showColumnSidebar, setShowColumnSidebar] = useState(false);
  const [interviewColumnVisibility, setInterviewColumnVisibility] = useState(
    () =>
      interviewColumns.reduce((acc, column) => {
        acc[column.key] = true;
        return acc;
      }, {})
  );
  const [showSubmittedColumnSidebar, setShowSubmittedColumnSidebar] = useState(false);
  const [submittedColumnVisibility, setSubmittedColumnVisibility] = useState(
    () =>
      allSubmittedColumnKeys.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
  );
  const [submittedPage, setSubmittedPage] = useState(1);
  const [submittedFilters, setSubmittedFilters] = useState({
    search: "",
    stage3Status: "active",
    startDate: null,
    endDate: null,
    interviewStatus: "",
    selectionMailStatus: "",
    selectionCallDone: "",
    photosStatus: "",
    profileStatus: "",
    panStatus: "",
    bankStatus: "",
    idCreatedStatus: "",
    idLiveStatus: "",
    agreementSignStatus: "",
    agreementUploadStatus: "",
    inductionStatus: "",
    finalStatus: "",
  });
  const submittedSearchDebounce = useDebounce(submittedFilters.search || "", 300);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [listAnalytics, setListAnalytics] = useState({});
  const [submittedForms, setSubmittedForms] = useState([]);
  const [submittedTotal, setSubmittedTotal] = useState(0);
  const [submittedDetails, setSubmittedDetails] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [activeTemplateType, setActiveTemplateType] = useState("interview");
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [modal, setModal] = useState(false);
  const [manualOnboardingModal, setManualOnboardingModal] = useState(false);
  const [addModalMode, setAddModalMode] = useState("interview");
  const [selectedData, setSelectedData] = useState(null);
  const [selectedStatusData, setSelectedStatusData] = useState(null);
  const [confirmStatusData, setConfirmStatusData] = useState(null);
  const [confirmDeleteData, setConfirmDeleteData] = useState(null);
  const [statusForm, setStatusForm] = useState({ selectionName: "", selectionPrice: "" });
  const [statusError, setStatusError] = useState({});
  const [personalMailData, setPersonalMailData] = useState(null);
  const [personalMailForm, setPersonalMailForm] = useState({ subject: "", body: "" });
  const [personalMailError, setPersonalMailError] = useState({});
  const [confirmPersonalMail, setConfirmPersonalMail] = useState(false);
  const [personalMailSending, setPersonalMailSending] = useState(false);
  const [rescheduleData, setRescheduleData] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    interviewDateTime: null,
    scheduleEmailAt: null,
    remark: "",
  });
  const [rescheduleError, setRescheduleError] = useState({});
  const [logsData, setLogsData] = useState(null);
  const [interviewDetailDrafts, setInterviewDetailDrafts] = useState({});
  const [selectedSubmittedIds, setSelectedSubmittedIds] = useState([]);
  const [submittedCommunicationModal, setSubmittedCommunicationModal] = useState(false);
  const [submittedCommunicationForm, setSubmittedCommunicationForm] = useState({
    channel: "email",
    subject: "Vedic Meet Onboarding Update",
    body: "Hi {{name}},\n\nPlease review your onboarding details using this link:\n{{formLink}}\n\nRegards,\nVedic Meet Team",
    campaignName: "",
  });
  const [submittedCommunicationError, setSubmittedCommunicationError] = useState({});
  const [correctionMailModal, setCorrectionMailModal] = useState(false);
  const [correctionMailForm, setCorrectionMailForm] = useState({
    submittedFormId: "",
    sections: [],
    sectionLabel: "",
    subject: "Vedic Meet onboarding correction required",
    remark: "",
    body: "Hi,\n\nPlease correct the rejected onboarding section using this link:\n{{formLink}}\n\nRegards,\nVedic Meet Team",
  });
  const [correctionMailError, setCorrectionMailError] = useState({});
  const [submittedRowDrafts, setSubmittedRowDrafts] = useState({});
  const [submittedRowFiles, setSubmittedRowFiles] = useState({});
  const [sheetUploading, setSheetUploading] = useState(false);
  const [submittedActionLoading, setSubmittedActionLoading] = useState(false);
  const [confirmSubmittedAction, setConfirmSubmittedAction] = useState(null);
  const [requestRemark, setRequestRemark] = useState({});
  const [assetRemark, setAssetRemark] = useState({});
  const [consultantRemark, setConsultantRemark] = useState("");
  const [formDataEditMode, setFormDataEditMode] = useState(false);
  const [formDataEdit, setFormDataEdit] = useState({});
  const [documentEditFiles, setDocumentEditFiles] = useState({});
  const [stage3Form, setStage3Form] = useState({
    agreementStatus: "pending",
    inductionStatus: "not_done",
    agreementFile: null,
  });
  const [reviewModal, setReviewModal] = useState(false);
  const [reviewConsultantId, setReviewConsultantId] = useState("");
  const [reviewFormData, setReviewFormData] = useState({
    review: "",
    rating: 5,
    userName: "",
    customDate: "",
    customTime: "",
    ratingType: "chat",
    reply: "",
  });
  const [reviewError, setReviewError] = useState({});
  const [reviewUserImage, setReviewUserImage] = useState(null);
  const [reviewUserImagePreview, setReviewUserImagePreview] = useState("");
  const [reviewButtonRef, reviewButtonLoader] = useButtonLoader("Add Review");
  const sheetInputRef = useRef(null);
  const [buttonref, buttonLoader] = useButtonLoader("Save");
  const [templateButtonRef, templateButtonLoader] = useButtonLoader("Save Template");
  const isOnboardingTab = tab === 1;
  const isConsultantDataTab = tab === 2;
  const isSubmittedFormsTab = isOnboardingTab || isConsultantDataTab;
  const activeInterviewDatePreset = getActiveDatePreset(listFilters.startDate, listFilters.endDate);
  const activeSubmittedDatePreset = getActiveDatePreset(
    submittedFilters.startDate,
    submittedFilters.endDate,
    ["today", "last7days"]
  );
  const submittedDateMax = endOfLocalDay(new Date());
  const activeSubmittedColumns = isConsultantDataTab ? consultantDataColumns : submittedColumns;
  const currentSubmittedStage3Status = isOnboardingTab
    ? "request"
    : submittedFilters.stage3Status;

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    if (newValue === 2 && submittedFilters.stage3Status === "request") {
      setSubmittedFilters((prev) => ({ ...prev, stage3Status: "active" }));
    }
  };

  const openSubmittedDetails = (item) => {
    setSubmittedDetails(item);
    setFormDataEditMode(false);
    setFormDataEdit(buildConsultantEditData(item));
    setDocumentEditFiles({});
    setConsultantRemark(item.consultantId?.approvalRemark || "");
    setRequestRemark(
      (item.requests || []).reduce((acc, request) => {
        acc[request._id] = request.review || "";
        return acc;
      }, {})
    );
    setAssetRemark({
      photos: item.assetReviews?.photos?.remark || "",
      video: item.assetReviews?.video?.remark || "",
    });
    setStage3Form({
      agreementStatus: item.stage3Controls?.agreementStatus || "pending",
      inductionStatus: item.stage3Controls?.inductionStatus || "not_done",
      agreementFile: null,
    });
  };

  const buildPreSelectedReturnState = (submittedFormId) => ({
    preSelectedReturn: {
      submittedFormId,
      tab,
      stage3Status: currentSubmittedStage3Status || "request",
      submittedPage,
    },
  });

  const fetchList = async (page = activePage) => {
    try {
      showLoader();
      const sendData = { page, limit: 10 };
      if (debounceValue) sendData.search = debounceValue;
      if (listFilters.status) sendData.status = listFilters.status;
      if (listFilters.emailStatus) sendData.emailStatus = listFilters.emailStatus;
      if (listFilters.selectionMailStatus) sendData.selectionMailStatus = listFilters.selectionMailStatus;
      if (listFilters.interviewConduct) sendData.interviewConduct = listFilters.interviewConduct;
      if (listFilters.startDate) {
        sendData.startDate = toDateFilterParam(listFilters.startDate);
      }
      if (listFilters.endDate) {
        sendData.endDate = toDateFilterParam(listFilters.endDate);
      }

      const { data } = await fetchPreSelectedConsultantApi(sendData);
      if (data.code === 200) {
        setList(data?.result?.list || []);
        setTotal(data?.result?.total || 0);
        setListAnalytics(data?.result?.analytics || {});
      } else {
        setList([]);
        setTotal(0);
        setListAnalytics({});
        toastMessage(data.message, "error", "preSelectedConsultantList");
      }
    } catch (err) {
      console.log(err);
    } finally {
      hideLoader();
    }
  };

  const fetchSubmittedForms = async (page = submittedPage) => {
    try {
      showLoader();
      const sendData = { page, limit: 10 };
      if (submittedSearchDebounce) sendData.search = submittedSearchDebounce;
      if (isSubmittedFormsTab && currentSubmittedStage3Status) {
        sendData.stage3Status = currentSubmittedStage3Status;
      }
      if (submittedFilters.startDate) {
        sendData.startDate = toDateFilterParam(submittedFilters.startDate);
      }
      if (submittedFilters.endDate) {
        sendData.endDate = toDateFilterParam(submittedFilters.endDate);
      }
      const onboardingFilterKeys = [
        "interviewStatus",
        "selectionMailStatus",
        "selectionCallDone",
        "photosStatus",
        "profileStatus",
        "panStatus",
        "bankStatus",
      ];
      const finalDataFilterKeys = [];
      const filterKeys = isConsultantDataTab
        ? finalDataFilterKeys
        : [...onboardingFilterKeys, ...finalDataFilterKeys];

      filterKeys.forEach((key) => {
        if (submittedFilters[key]) sendData[key] = submittedFilters[key];
      });

      const { data } = await fetchPreSelectedConsultantSubmittedFormsApi(sendData);
      if (data.code === 200) {
        const nextList = data?.result?.list || [];
        setSubmittedForms(nextList);
        setSubmittedTotal(data?.result?.total || 0);
        setListAnalytics(data?.result?.analytics || {});
        setSubmittedDetails((prev) => {
          if (!prev?._id) return prev;
          return nextList.find((item) => item._id === prev._id) || prev;
        });
        if (pendingSubmittedOpenRef.current) {
          const pendingId = pendingSubmittedOpenRef.current;
          const detail = nextList.find((item) => item._id === pendingId);
          if (detail) {
            pendingSubmittedOpenRef.current = null;
            openSubmittedDetails(detail);
          }
        }
      } else {
        setSubmittedForms([]);
        setSubmittedTotal(0);
        toastMessage(data.message, "error", "submittedFormsList");
      }
    } catch (err) {
      console.log(err);
    } finally {
      hideLoader();
    }
  };

  const refreshSubmittedList = () => {
    fetchSubmittedForms(submittedPage);
  };

  useEffect(() => {
    const returnState = location.state?.preSelectedReturn;
    if (!returnState?.submittedFormId) return;

    pendingSubmittedOpenRef.current = returnState.submittedFormId;
    setTab(returnState.stage3Status === "request" ? 1 : returnState.tab ?? 2);
    setSubmittedPage(returnState.submittedPage || 1);
    setSubmittedFilters((prev) => ({
      ...prev,
      stage3Status: returnState.stage3Status || "request",
    }));
    navigate(PRE_SELECTED_CONSULTANT, { replace: true, state: null });
  }, [location.state, navigate]);

  const fetchEmailTemplates = async () => {
    try {
      showLoader();
      const { data } = await fetchPreSelectedConsultantEmailTemplatesApi();
      if (data.code === 200) {
        setEmailTemplates(data?.result || []);
      } else {
        setEmailTemplates([]);
        toastMessage(data.message, "error", "emailTemplates");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "emailTemplates");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (activePage !== 1) setActivePage(1);
  }, [
    debounceValue,
    listFilters.status,
    listFilters.emailStatus,
    listFilters.selectionMailStatus,
    listFilters.interviewConduct,
    listFilters.startDate,
    listFilters.endDate,
  ]);

  useEffect(() => {
    fetchList();
  }, [
    activePage,
    debounceValue,
    listFilters.status,
    listFilters.emailStatus,
    listFilters.selectionMailStatus,
    listFilters.interviewConduct,
    listFilters.startDate,
    listFilters.endDate,
  ]);

  useEffect(() => {
    if (submittedPage !== 1) setSubmittedPage(1);
  }, [
    submittedSearchDebounce,
    submittedFilters.stage3Status,
    submittedFilters.startDate,
    submittedFilters.endDate,
    submittedFilters.interviewStatus,
    submittedFilters.selectionMailStatus,
    submittedFilters.selectionCallDone,
    submittedFilters.photosStatus,
    submittedFilters.profileStatus,
    submittedFilters.panStatus,
    submittedFilters.bankStatus,
    submittedFilters.idCreatedStatus,
    submittedFilters.idLiveStatus,
    submittedFilters.agreementSignStatus,
    submittedFilters.agreementUploadStatus,
    submittedFilters.inductionStatus,
    submittedFilters.finalStatus,
  ]);

  useEffect(() => {
    if (!isSubmittedFormsTab) return;
    fetchSubmittedForms();
  }, [
    isSubmittedFormsTab,
    submittedPage,
    submittedSearchDebounce,
    submittedFilters.stage3Status,
    submittedFilters.startDate,
    submittedFilters.endDate,
    submittedFilters.interviewStatus,
    submittedFilters.selectionMailStatus,
    submittedFilters.selectionCallDone,
    submittedFilters.photosStatus,
    submittedFilters.profileStatus,
    submittedFilters.panStatus,
    submittedFilters.bankStatus,
    submittedFilters.idCreatedStatus,
    submittedFilters.idLiveStatus,
    submittedFilters.agreementSignStatus,
    submittedFilters.agreementUploadStatus,
    submittedFilters.inductionStatus,
    submittedFilters.finalStatus,
    tab,
  ]);

  useEffect(() => {
    fetchEmailTemplates();
  }, []);

  const updateTemplateField = (type, key, value) => {
    setEmailTemplates((prev) =>
      prev.map((template) =>
        template.type === type ? { ...template, [key]: value } : template
      )
    );
  };

  const saveEmailTemplate = async (type) => {
    const template = emailTemplates.find((item) => item.type === type);
    if (!template?.subject?.trim()) {
      toastMessage("Subject is required", "error", "emailTemplates");
      return;
    }
    if (!template?.body?.trim()) {
      toastMessage("Email body is required", "error", "emailTemplates");
      return;
    }

    try {
      templateButtonLoader(true);
      const { data } = await updatePreSelectedConsultantEmailTemplateApi({
        type,
        subject: template.subject,
        body: template.body,
      });
      if (data.code === 200) {
        toastMessage(data.message, "success", "emailTemplates");
        fetchEmailTemplates();
      } else {
        toastMessage(data.message, "error", "emailTemplates");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "emailTemplates");
    } finally {
      templateButtonLoader(false);
    }
  };

  const openAddModal = () => {
    setSelectedData(null);
    setAddModalMode("interview");
    setModal(true);
  };

  const openOnboardingAddModal = () => {
    setSelectedData(null);
    setManualOnboardingModal(true);
  };

  const openEditModal = (data) => {
    setSelectedData(data);
    setAddModalMode("interview");
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    setSelectedData(null);
    setAddModalMode("interview");
  };

  const closeManualOnboardingModal = () => {
    setManualOnboardingModal(false);
  };

  const downloadSheetTemplate = () => {
    downloadTextFile(preSelectedSheetTemplateCsv, "pre-selected-consultant-template.csv");
  };

  const uploadSheetHandler = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedExtensions = [".xlsx", ".xls", ".csv"];
    const isAllowedFile = allowedExtensions.some((extension) =>
      file.name.toLowerCase().endsWith(extension)
    );

    if (!isAllowedFile) {
      toastMessage("Only .xlsx, .xls and .csv files are allowed", "error", "preSelectedSheet");
      event.target.value = "";
      return;
    }

    try {
      setSheetUploading(true);
      const formData = new FormData();
      formData.append("sheet", file);

      const { data } = await uploadPreSelectedConsultantSheetApi(formData);
      if (data.code === 200) {
        const result = data.result || {};
        const failedRows = (result.failedRows || []).slice(0, 3).map((item) => item.row).join(", ");
        const failedText = result.failedCount
          ? `, ${result.failedCount} row failed${failedRows ? ` (${failedRows})` : ""}`
          : "";
        toastMessage(
          `${result.insertedCount || 0} consultant uploaded${failedText}`,
          "success",
          "preSelectedSheet"
        );
        fetchList(1);
        setActivePage(1);
      } else {
        toastMessage(data.message, "error", "preSelectedSheet");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "preSelectedSheet");
    } finally {
      setSheetUploading(false);
      event.target.value = "";
    }
  };

  const updateInterviewStatus = async (payload) => {
    try {
      buttonLoader(true);
      const { data } = await updatePreSelectedConsultantInterviewStatusApi(payload);
      if (data.code === 200) {
        toastMessage(data.message, "success", "interviewStatus");
        fetchList();
        setSelectedStatusData(null);
        setConfirmStatusData(null);
        setStatusForm({ selectionName: "", selectionPrice: "" });
      } else {
        toastMessage(data.message, "error", "interviewStatus");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "interviewStatus");
    } finally {
      buttonLoader(false);
    }
  };

  const interviewStatusHandler = (item, value) => {
    if (value === (item.interviewStatus || "pending")) return;

    if (value === "selected") {
      setSelectedStatusData(item);
      setStatusForm({
        selectionName: item.selectionName || item.name || "",
        selectionPrice: item.selectionPrice || "",
      });
      setStatusError({});
      return;
    }

    setConfirmStatusData({ item, status: value });
  };

  const saveSelectedStatus = () => {
    const errors = {};
    if (!statusForm.selectionName?.trim()) errors.selectionName = "Name is required *";
    if (!statusForm.selectionPrice) errors.selectionPrice = "Price is required *";
    setStatusError(errors);
    if (Object.keys(errors).length) return;
    setConfirmStatusData({ item: selectedStatusData, status: "selected" });
  };

  const confirmStatusChange = () => {
    if (!confirmStatusData) return;
    if (confirmStatusData.status === "selected") {
      updateInterviewStatus({
        preSelectedConsultantId: confirmStatusData.item._id,
        interviewStatus: "selected",
        selectionName: statusForm.selectionName,
        selectionPrice: statusForm.selectionPrice,
      });
      return;
    }
    updateInterviewStatus({
      preSelectedConsultantId: confirmStatusData.item._id,
      interviewStatus: confirmStatusData.status,
    });
  };

  const isInterviewColumnVisible = (key) => interviewColumnVisibility[key] !== false;
  const visibleInterviewColumnCount = interviewColumns.filter((column) =>
    isInterviewColumnVisible(column.key)
  ).length;
  const isSubmittedColumnVisible = (key) => submittedColumnVisibility[key] !== false;
  const visibleSubmittedColumnCount = activeSubmittedColumns.filter((column) =>
    isSubmittedColumnVisible(column.key)
  ).length;

  const resetInterviewFilters = () => {
    setSearch("");
    setListFilters({
      status: "",
      emailStatus: "",
      selectionMailStatus: "",
      interviewConduct: "",
      startDate: null,
      endDate: null,
    });
  };

  const applyInterviewDateRange = (startDate, endDate) => {
    let start = startOfLocalDay(startDate);
    let end = endOfLocalDay(endDate);
    if (start && end && start.getTime() > end.getTime()) {
      end = endOfLocalDay(start);
    }
    setListFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
  };

  const setInterviewDatePreset = (preset) => {
    const range = getDatePresetRange(preset);
    setListFilters((prev) => ({ ...prev, startDate: range.startDate, endDate: range.endDate }));
  };

  const setAllInterviewColumns = (visible) => {
    setInterviewColumnVisibility(
      interviewColumns.reduce((acc, column) => {
        acc[column.key] = column.alwaysVisible ? true : visible;
        return acc;
      }, {})
    );
  };

  const resetInterviewColumns = () => {
    setAllInterviewColumns(true);
  };

  const setAllSubmittedColumns = (visible) => {
    setSubmittedColumnVisibility(
      activeSubmittedColumns.reduce((acc, column) => {
        acc[column.key] = column.alwaysVisible ? true : visible;
        return acc;
      }, { ...submittedColumnVisibility })
    );
  };

  const resetSubmittedColumns = () => {
    setAllSubmittedColumns(true);
  };

  const resetSubmittedFilters = () => {
    setSubmittedFilters((prev) => ({
      ...prev,
      search: "",
      startDate: null,
      endDate: null,
      interviewStatus: "",
      selectionMailStatus: "",
      selectionCallDone: "",
      photosStatus: "",
      profileStatus: "",
      panStatus: "",
      bankStatus: "",
      idCreatedStatus: "",
      idLiveStatus: "",
      agreementSignStatus: "",
      agreementUploadStatus: "",
      inductionStatus: "",
      finalStatus: "",
    }));
  };

  const applySubmittedDateRange = (startDate, endDate) => {
    const range = clampRangeToToday(startDate, endDate);
    setSubmittedFilters((prev) => ({ ...prev, startDate: range.startDate, endDate: range.endDate }));
  };

  const setSubmittedDatePreset = (preset) => {
    const safePreset = preset === "today" ? "today" : "last7days";
    const picked = getDatePresetRange(safePreset);
    const range = clampRangeToToday(picked.startDate, picked.endDate);
    setSubmittedFilters((prev) => ({ ...prev, startDate: range.startDate, endDate: range.endDate }));
  };

  const updateInterviewRowState = (id, updates) => {
    setList((prev) => prev.map((item) => (item._id === id ? { ...item, ...updates } : item)));
  };

  const getInterviewDraftValue = (item, key) => {
    const draft = interviewDetailDrafts[item._id] || {};
    if (draft[key] !== undefined) return draft[key];
    if (key === "pricePerMin") return item.pricePerMin ?? item.selectionPrice ?? "";
    return item[key] ?? "";
  };

  const updateInterviewDetailDraft = (item, key, value) => {
    setInterviewDetailDrafts((prev) => ({
      ...prev,
      [item._id]: {
        ...(prev[item._id] || {}),
        [key]: value,
      },
    }));
  };

  const updateInterviewDetails = async (item, updates) => {
    if (!item?._id) return;
    updateInterviewRowState(item._id, updates);
    try {
      const { data } = await updatePreSelectedConsultantInterviewDetailsApi({
        preSelectedConsultantId: item._id,
        ...updates,
      });
      if (data.code === 200) {
        updateInterviewRowState(item._id, data.result || updates);
        toastMessage(data.message, "success", "interviewDetails");
      } else {
        updateInterviewRowState(item._id, item);
        toastMessage(data.message, "error", "interviewDetails");
      }
    } catch (err) {
      console.log(err);
      updateInterviewRowState(item._id, item);
      toastMessage("Something went wrong", "error", "interviewDetails");
    }
  };

  const saveInterviewDetails = async (item) => {
    if (!item?._id) return;
    const draft = interviewDetailDrafts[item._id] || {};
    const payload = {
      interviewConduct: draft.interviewConduct ?? item.interviewConduct ?? "pending",
      selectionRejectionReason:
        draft.selectionRejectionReason ?? item.selectionRejectionReason ?? "",
      interviewScore: draft.interviewScore ?? item.interviewScore ?? "",
      usp: draft.usp ?? item.usp ?? "",
      pricePerMin: draft.pricePerMin ?? item.pricePerMin ?? item.selectionPrice ?? "",
    };

    await updateInterviewDetails(item, payload);
    setInterviewDetailDrafts((prev) => {
      const next = { ...prev };
      delete next[item._id];
      return next;
    });
  };

  const requestDeletePreSelectedConsultant = (item) => {
    if (!item?._id) return;
    setConfirmDeleteData(item);
  };

  const deletePreSelectedConsultant = async () => {
    const item = confirmDeleteData;
    if (!item?._id) return;
    try {
      const { data } = await deletePreSelectedConsultantApi({
        preSelectedConsultantId: item._id,
      });
      if (data.code === 200) {
        toastMessage(data.message, "success", "preSelectedConsultantDelete");
        setConfirmDeleteData(null);
        setList((prev) => prev.filter((row) => row._id !== item._id));
        setTotal((prev) => Math.max(0, prev - 1));
        fetchList(activePage);
      } else {
        toastMessage(data.message, "error", "preSelectedConsultantDelete");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "preSelectedConsultantDelete");
    }
  };

  const openPersonalMailModal = (item) => {
    setPersonalMailData(item);
    setPersonalMailForm({
      subject: "",
      body: `<p>Hi {{name}},</p><p></p><p>Regards,<br/>Vedic Meet Team</p>`,
    });
    setPersonalMailError({});
    setConfirmPersonalMail(false);
  };

  const closePersonalMailModal = () => {
    setPersonalMailData(null);
    setPersonalMailForm({ subject: "", body: "" });
    setPersonalMailError({});
    setConfirmPersonalMail(false);
    setPersonalMailSending(false);
  };

  const updatePersonalMailField = (key, value) => {
    setPersonalMailForm((prev) => ({ ...prev, [key]: value }));
    if (personalMailError[key]) {
      setPersonalMailError((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const confirmPersonalMailHandler = () => {
    const errors = {};
    if (!personalMailForm.subject?.trim()) errors.subject = "Subject is required *";
    if (!personalMailForm.body?.trim() || personalMailForm.body === "<p><br></p>") {
      errors.body = "Email body is required *";
    }
    setPersonalMailError(errors);
    if (Object.keys(errors).length) return;
    setConfirmPersonalMail(true);
  };

  const sendPersonalizedMail = async () => {
    if (!personalMailData?._id) return;
    try {
      setPersonalMailSending(true);
      const { data } = await sendPreSelectedConsultantPersonalizedEmailApi({
        preSelectedConsultantId: personalMailData._id,
        subject: personalMailForm.subject,
        body: personalMailForm.body,
      });
      if (data.code === 200) {
        toastMessage(data.message, "success", "personalizedEmail");
        fetchList();
        closePersonalMailModal();
      } else {
        toastMessage(data.message, "error", "personalizedEmail");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "personalizedEmail");
    } finally {
      setPersonalMailSending(false);
    }
  };

  const toggleSubmittedSelection = (id) => {
    setSelectedSubmittedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleAllSubmittedSelection = () => {
    const pageIds = submittedForms.map((item) => item._id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedSubmittedIds.includes(id));
    setSelectedSubmittedIds((prev) =>
      allSelected
        ? prev.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...prev, ...pageIds]))
    );
  };

  const updateSubmittedCommunicationField = (key, value) => {
    setSubmittedCommunicationForm((prev) => ({ ...prev, [key]: value }));
    if (submittedCommunicationError[key]) {
      setSubmittedCommunicationError((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const sendSubmittedCommunication = async () => {
    const errors = {};
    if (!selectedSubmittedIds.length) errors.selected = "Select at least one consultant";
    if (["email", "both"].includes(submittedCommunicationForm.channel)) {
      if (!submittedCommunicationForm.subject?.trim()) errors.subject = "Subject is required";
      if (!submittedCommunicationForm.body?.trim()) errors.body = "Body is required";
    }
    if (["whatsapp", "both"].includes(submittedCommunicationForm.channel)) {
      if (!submittedCommunicationForm.campaignName?.trim()) {
        errors.campaignName = "AiSensy campaign name is required";
      }
    }
    setSubmittedCommunicationError(errors);
    if (Object.keys(errors).length) {
      if (errors.selected) toastMessage(errors.selected, "error", "submittedCommunication");
      return;
    }

    try {
      setSubmittedActionLoading(true);
      const { data } = await sendPreSelectedConsultantSubmittedCommunicationApi({
        submittedFormIds: selectedSubmittedIds,
        ...submittedCommunicationForm,
      });
      if (data.code === 200) {
        toastMessage(
          `${data.result?.sent || 0} sent, ${data.result?.failed || 0} failed`,
          "success",
          "submittedCommunication"
        );
        setSubmittedCommunicationModal(false);
        setSelectedSubmittedIds([]);
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "submittedCommunication");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedCommunication");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const openCorrectionMailModal = ({ sections, sectionLabel, remark = "" }) => {
    if (!submittedDetails?._id) return;
    setCorrectionMailForm({
      submittedFormId: submittedDetails._id,
      sections,
      sectionLabel,
      subject: "Vedic Meet onboarding correction required",
      remark,
      body: `Hi,\n\nPlease correct the rejected ${sectionLabel} section using this link:\n{{formLink}}\n\nRegards,\nVedic Meet Team`,
    });
    setCorrectionMailError({});
    setCorrectionMailModal(true);
  };

  const updateCorrectionMailField = (key, value) => {
    setCorrectionMailForm((prev) => ({ ...prev, [key]: value }));
    if (correctionMailError[key]) {
      setCorrectionMailError((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const sendCorrectionMail = async () => {
    const errors = {};
    if (!correctionMailForm.subject?.trim()) errors.subject = "Subject is required";
    if (!correctionMailForm.remark?.trim()) errors.remark = "Remark is required";
    setCorrectionMailError(errors);
    if (Object.keys(errors).length) return;

    try {
      setSubmittedActionLoading(true);
      const { data } = await sendPreSelectedConsultantCorrectionMailApi(correctionMailForm);
      if (data.code === 200) {
        toastMessage(data.message, "success", "correctionMail");
        setSubmittedDetails((prev) =>
          prev ? { ...prev, correctionRequest: data.result?.correctionRequest, correctionLogs: data.result?.correctionLogs } : prev
        );
        refreshSubmittedList();
        setCorrectionMailModal(false);
      } else {
        toastMessage(data.message, "error", "correctionMail");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "correctionMail");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const getSubmittedDraftValue = (item, key, fallback = "") => {
    const draft = submittedRowDrafts[item._id] || {};
    if (draft[key] !== undefined) return draft[key];
    return getOnboardingControlValue(item, key, fallback);
  };

  const updateSubmittedRowDraft = (item, key, value) => {
    setSubmittedRowDrafts((prev) => ({
      ...prev,
      [item._id]: {
        ...(prev[item._id] || {}),
        [key]: value,
      },
    }));
  };

  const updateSubmittedRowFile = (item, key, file) => {
    setSubmittedRowFiles((prev) => ({
      ...prev,
      [item._id]: {
        ...(prev[item._id] || {}),
        [key]: file,
      },
    }));
  };

  const reviewToOnboardingStatus = (review, fallback = "pending") => {
    if (review?.status === "approved") return "accept";
    if (review?.status === "rejected") return "reject";
    return fallback;
  };

  const requestTypeToOnboardingStatus = (requests = [], type, fallback = "pending") => {
    const request = requests.find((item) => item.requestType === type);
    if (!request) return fallback;
    if (Number(request.approveStatus) === 2) return "accept";
    if (Number(request.approveStatus) === 3) return "reject";
    return "pending";
  };

  const consultantReviewToOnboardingStatus = (consultant = {}) => {
    if (consultant.isAdminVerify) return "accept";
    if (consultant.disapprovedAt || consultant.approvalRemark) return "reject";
    return "pending";
  };

  const mapYesNoToStage3Agreement = (value) => {
    if (value === "yes") return "done";
    if (value === "no") return "declined";
    return "pending";
  };

  const mapYesNoToStage3Induction = (value) => (value === "yes" ? "done" : "not_done");

  const saveSubmittedOnboardingRow = async (item) => {
    if (!item?._id) return;
    const consultant = item.consultantId || {};
    const preSelected = item.preSelectedConsultantId || {};
    const draft = submittedRowDrafts[item._id] || {};
    const rowFiles = submittedRowFiles[item._id] || {};
    const controls = {
      selectionMailSent: draft.selectionMailSent ?? getOnboardingControlValue(item, "selectionMailSent", preSelected.interviewStatusMailSentAt ? "sent" : "not_sent"),
      selectionCallDate: draft.selectionCallDate ?? getOnboardingControlValue(item, "selectionCallDate", ""),
      selectionCallDone: draft.selectionCallDone ?? getOnboardingControlValue(item, "selectionCallDone", "pending"),
      idCreatedDate: draft.idCreatedDate ?? getOnboardingControlValue(item, "idCreatedDate", ""),
      idCreatedStatus: draft.idCreatedStatus ?? getOnboardingControlValue(item, "idCreatedStatus", consultant.userId ? "yes" : "pending"),
      idLiveDate: draft.idLiveDate ?? getOnboardingControlValue(item, "idLiveDate", ""),
      idLiveStatus: draft.idLiveStatus ?? getOnboardingControlValue(item, "idLiveStatus", getStage3FieldValue("liveDate", item) ? "yes" : "pending"),
      agreementDate: draft.agreementDate ?? getOnboardingControlValue(item, "agreementDate", ""),
      agreementSignStatus: draft.agreementSignStatus ?? getOnboardingControlValue(item, "agreementSignStatus", item.stage3Controls?.agreementStatus === "done" ? "yes" : "pending"),
      agreementUpload: rowFiles.agreement
        ? "yes"
        : draft.agreementUpload ?? getOnboardingControlValue(item, "agreementUpload", item.stage3Controls?.agreementFile ? "yes" : "pending"),
      inductionDate: draft.inductionDate ?? getOnboardingControlValue(item, "inductionDate", ""),
      inductionStatus: draft.inductionStatus ?? getOnboardingControlValue(item, "inductionStatus", item.stage3Controls?.inductionStatus === "done" ? "yes" : "pending"),
      finalStatus: draft.finalStatus ?? getOnboardingControlValue(item, "finalStatus", "pending"),
    };
    const shouldUpdateStage3 =
      rowFiles.agreement ||
      draft.agreementSignStatus !== undefined ||
      draft.inductionStatus !== undefined;

    try {
      setSubmittedActionLoading(true);
      const { data } = await updatePreSelectedConsultantSubmittedOnboardingRowApi({
        submittedFormId: item._id,
        controls,
      });
      if (data.code === 200) {
        let nextStage3Controls = item.stage3Controls || {};
        if (shouldUpdateStage3) {
          const stage3Payload = new FormData();
          stage3Payload.append("submittedFormId", item._id);
          stage3Payload.append("agreementStatus", mapYesNoToStage3Agreement(controls.agreementSignStatus));
          stage3Payload.append("inductionStatus", mapYesNoToStage3Induction(controls.inductionStatus));
          if (rowFiles.agreement) stage3Payload.append("agreement", rowFiles.agreement);
          const stage3Response = await updatePreSelectedConsultantStage3Api(stage3Payload);
          if (stage3Response.data.code !== 200) {
            toastMessage(stage3Response.data.message, "error", "stage3Controls");
            return;
          }
          nextStage3Controls = stage3Response.data.result || nextStage3Controls;
        }
        toastMessage(data.message, "success", "submittedOnboardingRow");
        setSubmittedForms((prev) =>
          prev.map((row) =>
            row._id === item._id
              ? {
                  ...row,
                  onboardingControls: data.result?.onboardingControls || controls,
                  stage3Controls: nextStage3Controls,
                }
              : row
          )
        );
        setSubmittedRowDrafts((prev) => {
          const next = { ...prev };
          delete next[item._id];
          return next;
        });
        setSubmittedRowFiles((prev) => {
          const next = { ...prev };
          delete next[item._id];
          return next;
        });
        if (controls.finalStatus === "live") {
          refreshSubmittedList();
        }
      } else {
        toastMessage(data.message, "error", "submittedOnboardingRow");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedOnboardingRow");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const openRescheduleModal = (item) => {
    setRescheduleData(item);
    setRescheduleForm({
      interviewDateTime: item.interviewDateTime ? new Date(item.interviewDateTime) : null,
      scheduleEmailAt: item.scheduleEmailAt ? new Date(item.scheduleEmailAt) : null,
      remark: "",
    });
    setRescheduleError({});
  };

  const closeRescheduleModal = () => {
    setRescheduleData(null);
    setRescheduleForm({ interviewDateTime: null, scheduleEmailAt: null, remark: "" });
    setRescheduleError({});
  };

  const updateRescheduleField = (key, value) => {
    setRescheduleForm((prev) => ({ ...prev, [key]: value }));
    if (rescheduleError[key]) setRescheduleError((prev) => ({ ...prev, [key]: "" }));
  };

  const saveRescheduleMail = async () => {
    const errors = {};
    if (!rescheduleForm.interviewDateTime) errors.interviewDateTime = "Interview date & time is required *";
    if (!rescheduleForm.scheduleEmailAt) errors.scheduleEmailAt = "Send mail time is required *";
    setRescheduleError(errors);
    if (Object.keys(errors).length) return;

    try {
      buttonLoader(true);
      const { data } = await reschedulePreSelectedConsultantInterviewMailApi({
        preSelectedConsultantId: rescheduleData._id,
        interviewDateTime: new Date(rescheduleForm.interviewDateTime).toISOString(),
        scheduleEmailAt: new Date(rescheduleForm.scheduleEmailAt).toISOString(),
        remark: rescheduleForm.remark || "",
      });
      if (data.code === 200) {
        toastMessage(data.message, "success", "rescheduleInterviewMail");
        updateInterviewRowState(rescheduleData._id, data.result || {});
        closeRescheduleModal();
      } else {
        toastMessage(data.message, "error", "rescheduleInterviewMail");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "rescheduleInterviewMail");
    } finally {
      buttonLoader(false);
    }
  };

  const updateSubmittedRequestState = (requestId, updates) => {
    setSubmittedDetails((prev) => {
      if (!prev?.requests) return prev;
      return {
        ...prev,
        requests: prev.requests.map((request) =>
          request._id === requestId ? { ...request, ...updates } : request
        ),
      };
    });
  };

  const updateSubmittedAssetReviewState = (assetType, updates) => {
    setSubmittedDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        assetReviews: {
          ...(prev.assetReviews || {}),
          [assetType]: {
            ...(prev.assetReviews?.[assetType] || {}),
            ...updates,
          },
        },
      };
    });
  };

  const executeSubmittedRequestAction = async (requestId, approveStatus) => {
    if (!requestId) return;
    if (Number(approveStatus) === 3 && !requestRemark[requestId]?.trim()) {
      toastMessage("Remark is required for reject", "error", "submittedRequestAction");
      return;
    }

    try {
      setSubmittedActionLoading(true);
      const { data } = await requestAcceptRejectApi({
        requestId,
        approveStatus,
        remark: requestRemark[requestId] || "",
      });
      if (data.code === 200) {
        toastMessage(data.message, "success", "submittedRequestAction");
        updateSubmittedRequestState(requestId, {
          approveStatus,
          review: requestRemark[requestId] || "",
        });
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "submittedRequestAction");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedRequestAction");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const executeSubmittedConsultantAction = async (consultantId, approve) => {
    if (!consultantId) return;
    if (!approve && !consultantRemark?.trim()) {
      toastMessage("Remark is required for reject", "error", "submittedConsultantAction");
      return;
    }

    try {
      setSubmittedActionLoading(true);
      const { data } = await approveConsultantStatus({
        consultantId,
        approve,
        remark: consultantRemark || "",
      });
      if (data.code === 200) {
        toastMessage(data.message, "success", "submittedConsultantAction");
        updateSubmittedConsultantState({
          ...(data.result || {}),
          isAdminVerify: approve,
          isBin: false,
          approvalActionAt: new Date().toISOString(),
          ...(approve
            ? { approvedAt: new Date().toISOString(), disapprovedAt: null, approvalRemark: "" }
            : { disapprovedAt: new Date().toISOString(), approvalRemark: consultantRemark || "" }),
        });
        updateSubmittedListConsultantState(consultantId, {
          ...(data.result || {}),
          isAdminVerify: approve,
          isBin: false,
        });
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "submittedConsultantAction");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedConsultantAction");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const updateSubmittedConsultantState = (updates) => {
    setSubmittedDetails((prev) => {
      if (!prev?.consultantId) return prev;
      return {
        ...prev,
        consultantId: {
          ...prev.consultantId,
          ...updates,
        },
      };
    });
  };

  const updateSubmittedListConsultantState = (consultantId, updates) => {
    setSubmittedForms((prev) =>
      prev.map((item) =>
        getConsultantRecordId(item) === consultantId
          ? {
              ...item,
              consultantId: {
                ...(typeof item.consultantId === "object" ? item.consultantId : {}),
                ...updates,
              },
            }
          : item
      )
    );
  };

  const executeSubmittedConsultantBin = async (consultantId) => {
    if (!consultantId) return;
    if (!consultantRemark?.trim()) {
      toastMessage("Remark is required for bin", "error", "submittedConsultantBin");
      return;
    }

    try {
      setSubmittedActionLoading(true);
      const { data } = await moveConsultantToBinApi({
        consultantId,
        remark: consultantRemark.trim(),
      });
      if (data.code === 200) {
        toastMessage("Consultant moved to bin successfully", "success", "submittedConsultantBin");
        updateSubmittedConsultantState({
          isBin: true,
          isAdminVerify: false,
          binnedAt: new Date().toISOString(),
          binnedRemark: consultantRemark.trim(),
        });
        setSubmittedForms((prev) => prev.filter((item) => getConsultantRecordId(item) !== consultantId));
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "submittedConsultantBin");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedConsultantBin");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const executeSubmittedConsultantRestore = async (consultantId) => {
    if (!consultantId) return;

    try {
      setSubmittedActionLoading(true);
      const { data } = await restoreConsultantFromBinApi({ consultantId });
      if (data.code === 200) {
        toastMessage("Consultant restored to consultant request successfully", "success", "submittedConsultantRestore");
        updateSubmittedConsultantState({
          isBin: false,
          isAdminVerify: false,
          restoredAt: new Date().toISOString(),
        });
        setSubmittedDetails(null);
        refreshSubmittedList();
      } else {
        toastMessage(data.message || "Unable to restore consultant from bin", "error", "submittedConsultantRestore");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedConsultantRestore");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const executeSubmittedConsultantStatusToggle = async (consultantId, currentStatus) => {
    if (!consultantId) return;

    try {
      setSubmittedActionLoading(true);
      const nextStatus = !currentStatus;
      const { data } = await changeConsultantStatus({
        consultantId,
        status: nextStatus,
      });
      if (data.code === 200) {
        toastMessage(
          nextStatus ? "Consultant Unblocked successfully" : "Consultant Blocked successfully",
          "success",
          "submittedConsultantStatus"
        );
        updateSubmittedConsultantState({
          status: nextStatus,
          statusUpdatedAt: data?.result?.statusUpdatedAt || new Date().toISOString(),
        });
        updateSubmittedListConsultantState(consultantId, {
          status: nextStatus,
          statusUpdatedAt: data?.result?.statusUpdatedAt || new Date().toISOString(),
        });
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "submittedConsultantStatus");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedConsultantStatus");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const executeSubmittedConsultantFakeToggle = async (consultantId, currentFakeStatus) => {
    if (!consultantId) return;

    try {
      setSubmittedActionLoading(true);
      const nextFakeStatus = !currentFakeStatus;
      const { data } = await changeConsultantStatusisFake({
        consultantId,
        isFake: nextFakeStatus,
      });
      if (data.code === 200) {
        toastMessage(data.message, "success", "submittedConsultantFake");
        updateSubmittedConsultantState({ isFake: nextFakeStatus });
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "submittedConsultantFake");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedConsultantFake");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const executeSubmittedAssetAction = async (assetType, reviewStatus, mediaKey = "") => {
    if (!submittedDetails?._id) return;
    if (reviewStatus === "rejected" && !assetRemark[assetType]?.trim()) {
      toastMessage("Remark is required for reject", "error", "submittedAssetReview");
      return;
    }

    try {
      setSubmittedActionLoading(true);
      const { data } = await updatePreSelectedConsultantAssetReviewApi({
        submittedFormId: submittedDetails._id,
        assetType,
        reviewStatus,
        remark: assetRemark[assetType] || "",
        mediaKey,
      });
      if (data.code === 200) {
        toastMessage(data.message, "success", "submittedAssetReview");
        updateSubmittedAssetReviewState(assetType, data.result || {
          status: reviewStatus,
          remark: assetRemark[assetType] || "",
          updatedAt: new Date().toISOString(),
        });
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "submittedAssetReview");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedAssetReview");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const updateStage3State = (updates) => {
    setSubmittedDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stage3Controls: {
          ...(prev.stage3Controls || {}),
          ...updates,
        },
      };
    });
  };

  const handleStage3FileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setStage3Form((prev) => ({ ...prev, agreementFile: file }));
  };

  const saveStage3Controls = async () => {
    if (!submittedDetails?._id) return;

    try {
      setSubmittedActionLoading(true);
      const formData = new FormData();
      formData.append("submittedFormId", submittedDetails._id);
      formData.append("agreementStatus", stage3Form.agreementStatus || "pending");
      formData.append("inductionStatus", stage3Form.inductionStatus || "not_done");
      if (stage3Form.agreementFile) formData.append("agreement", stage3Form.agreementFile);

      const { data } = await updatePreSelectedConsultantStage3Api(formData);
      if (data.code === 200) {
        toastMessage(data.message, "success", "stage3Controls");
        updateStage3State(data.result || {});
        setStage3Form((prev) => ({ ...prev, agreementFile: null }));
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "stage3Controls");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "stage3Controls");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const saveSubmittedConsultantData = async () => {
    if (!submittedDetails?._id) return;

    try {
      setSubmittedActionLoading(true);
      const payload = new FormData();
      payload.append("submittedFormId", submittedDetails._id);
      payload.append("consultantData", JSON.stringify(formDataEdit));
      Object.entries(documentEditFiles).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((file) => payload.append(key, file));
        } else if (value) {
          payload.append(key, value);
        }
      });
      const { data } = await updatePreSelectedConsultantSubmittedConsultantDataApi(payload);
      if (data.code === 200) {
        toastMessage(data.message, "success", "submittedConsultantData");
        setSubmittedDetails((prev) => (prev ? { ...prev, consultantId: data.result || prev.consultantId } : prev));
        setSubmittedForms((prev) =>
          prev.map((item) =>
            item._id === submittedDetails._id ? { ...item, consultantId: data.result || item.consultantId } : item
          )
        );
        setFormDataEditMode(false);
        setDocumentEditFiles({});
        refreshSubmittedList();
      } else {
        toastMessage(data.message, "error", "submittedConsultantData");
      }
    } catch (err) {
      console.log(err);
      toastMessage("Something went wrong", "error", "submittedConsultantData");
    } finally {
      setSubmittedActionLoading(false);
    }
  };

  const openReviewModal = (consultantId) => {
    setReviewConsultantId(consultantId);
    setReviewFormData({
      review: "",
      rating: 5,
      userName: "",
      customDate: "",
      customTime: "",
      ratingType: "chat",
      reply: "",
    });
    setReviewError({});
    setReviewUserImage(null);
    setReviewUserImagePreview("");
    setReviewModal(true);
  };

  const closeReviewModal = () => {
    setReviewModal(false);
    setReviewConsultantId("");
    setReviewUserImage(null);
    setReviewUserImagePreview("");
    setReviewError({});
  };

  const handleReviewInputChange = (event) => {
    const { name, value } = event.target;
    setReviewFormData((prev) => ({ ...prev, [name]: value }));
    if (reviewError[name]) {
      setReviewError((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleReviewImageChange = (event) => {
    const file = event.target.files?.[0];
    setReviewUserImage(file || null);
    if (!file) {
      setReviewUserImagePreview("");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setReviewUserImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const addReviewHandler = async () => {
    const errors = {};
    if (!reviewFormData.review?.trim()) errors.review = "Review is required";
    if (!reviewFormData.rating || reviewFormData.rating < 1 || reviewFormData.rating > 5) {
      errors.rating = "Rating must be between 1 and 5";
    }
    if (reviewFormData.customDate && !reviewFormData.customTime) {
      errors.customTime = "Time is required when date is selected";
    }
    setReviewError(errors);
    if (Object.keys(errors).length) return;

    try {
      reviewButtonLoader(true);
      const formData = new FormData();
      formData.append("consultantId", reviewConsultantId);
      formData.append("review", reviewFormData.review);
      formData.append("rating", reviewFormData.rating);
      formData.append("userName", reviewFormData.userName || "");
      formData.append("customDate", reviewFormData.customDate || "");
      formData.append("customTime", reviewFormData.customTime || "");
      formData.append("ratingType", reviewFormData.ratingType || "chat");
      formData.append("reply", reviewFormData.reply || "");
      if (reviewUserImage) formData.append("userImage", reviewUserImage);

      const { data } = await addReviewByAdminApi(formData);
      if (data.code === 200) {
        toastMessage(data.message || "Review added successfully", "success", "stage3Review");
        setSubmittedDetails((prev) => (prev ? { ...prev, reviewCount: (prev.reviewCount || 0) + 1 } : prev));
        refreshSubmittedList();
        closeReviewModal();
      } else {
        toastMessage(data.message || "Failed to add review", "error", "stage3Review");
      }
    } catch (err) {
      console.log(err);
      toastMessage("An error occurred while adding review", "error", "stage3Review");
    } finally {
      reviewButtonLoader(false);
    }
  };

  const requestSubmittedRequestAction = (requestOrId, approveStatus) => {
    const request = typeof requestOrId === "object" ? requestOrId : null;
    const requestId = request?._id || requestOrId;
    const label = requestTypeLabel(request?.requestType);
    if (Number(approveStatus) === 3 && !requestRemark[requestId]?.trim()) {
      toastMessage("Remark is required for reject", "error", "submittedRequestAction");
      return;
    }
    setConfirmSubmittedAction({
      type: "request",
      requestId,
      approveStatus,
      title: approveStatus === 2 ? `Approve ${label}` : `Reject ${label}`,
      description:
        approveStatus === 2
          ? `Do you want to approve this ${label} request?`
          : `Do you want to reject this ${label} request?`,
      actionLabel: approveStatus === 2 ? "Approve" : "Reject",
      actionClass: approveStatus === 2 ? "btn-success" : "btn-danger",
    });
  };

  const requestSubmittedConsultantAction = (consultantId, approve) => {
    if (!approve && !consultantRemark?.trim()) {
      toastMessage("Remark is required for reject", "error", "submittedConsultantAction");
      return;
    }
    setConfirmSubmittedAction({
      type: "consultantApproval",
      consultantId,
      approve,
      title: approve ? "Approve Consultant" : "Reject Consultant",
      description: approve
        ? "Do you want to approve this consultant?"
        : "Do you want to reject this consultant?",
      actionLabel: approve ? "Approve" : "Reject",
      actionClass: approve ? "btn-success" : "btn-danger",
    });
  };

  const requestSubmittedConsultantBin = (consultantId) => {
    setConfirmSubmittedAction({
      type: "consultantBin",
      consultantId,
      title: "Move Consultant To Bin",
      description: "Do you want to move this consultant to bin? Please add a remark before moving.",
      actionLabel: "Move To Bin",
      actionClass: "btn-secondary",
    });
  };

  const requestSubmittedConsultantRestore = (consultantId) => {
    setConfirmSubmittedAction({
      type: "consultantRestore",
      consultantId,
      title: "Restore Consultant To Request",
      description: "Do you want to restore this consultant to Consultant Request?",
      actionLabel: "Restore",
      actionClass: "btn-primary",
    });
  };

  const requestSubmittedConsultantStatusToggle = (consultantId, currentStatus) => {
    setConfirmSubmittedAction({
      type: "consultantStatus",
      consultantId,
      currentStatus,
      title: currentStatus ? "Block Consultant" : "Unblock Consultant",
      description: currentStatus
        ? "Do you want to block this consultant?"
        : "Do you want to unblock this consultant?",
      actionLabel: currentStatus ? "Block" : "Unblock",
      actionClass: currentStatus ? "btn-danger" : "btn-success",
    });
  };

  const requestSubmittedConsultantFakeToggle = (consultantId, currentFakeStatus) => {
    setConfirmSubmittedAction({
      type: "consultantFake",
      consultantId,
      currentFakeStatus,
      title: currentFakeStatus ? "Mark Consultant Real" : "Mark Consultant Fake",
      description: currentFakeStatus
        ? "Do you want to mark this consultant as real?"
        : "Do you want to mark this consultant as fake?",
      actionLabel: currentFakeStatus ? "Mark Real" : "Mark Fake",
      actionClass: currentFakeStatus ? "btn-success" : "btn-danger",
    });
  };

  const requestSubmittedAssetAction = (assetType, reviewStatus, mediaKey = "") => {
    if (reviewStatus === "rejected" && !assetRemark[assetType]?.trim()) {
      toastMessage("Remark is required for reject", "error", "submittedAssetReview");
      return;
    }
    setConfirmSubmittedAction({
      type: "assetReview",
      assetType,
      reviewStatus,
      mediaKey,
      title: reviewStatus === "approved" ? "Approve Media" : "Reject Media",
      description:
        reviewStatus === "approved"
          ? `Do you want to approve this ${mediaKey ? "photo" : "media section"}?`
          : `Do you want to reject this ${mediaKey ? "photo" : "media section"}?`,
      actionLabel: reviewStatus === "approved" ? "Approve" : "Reject",
      actionClass: reviewStatus === "approved" ? "btn-success" : "btn-danger",
    });
  };

  const confirmSubmittedActionHandler = async () => {
    const action = confirmSubmittedAction;
    if (!action) return;

    if (action.type === "consultantBin" && !consultantRemark?.trim()) {
      toastMessage("Remark is required for bin", "error", "submittedConsultantBin");
      return;
    }

    if (action.type === "request") {
      await executeSubmittedRequestAction(action.requestId, action.approveStatus);
    }
    if (action.type === "consultantApproval") {
      await executeSubmittedConsultantAction(action.consultantId, action.approve);
    }
    if (action.type === "consultantBin") {
      await executeSubmittedConsultantBin(action.consultantId);
    }
    if (action.type === "consultantRestore") {
      await executeSubmittedConsultantRestore(action.consultantId);
    }
    if (action.type === "consultantStatus") {
      await executeSubmittedConsultantStatusToggle(action.consultantId, action.currentStatus);
    }
    if (action.type === "consultantFake") {
      await executeSubmittedConsultantFakeToggle(action.consultantId, action.currentFakeStatus);
    }
    if (action.type === "assetReview") {
      await executeSubmittedAssetAction(action.assetType, action.reviewStatus, action.mediaKey);
    }

    setConfirmSubmittedAction(null);
  };

  return (
    <>
      {loader}
      {manualOnboardingModal ? (
        <div className="WrapperArea pre-selected-consultant-page">
          <div className="WrapperBox pre-selected-consultant-box">
            <SelectedConsultantFormContent
              embedded
              shouldFetchDetails={false}
              submitApi={(token, formData) => submitManualPreSelectedConsultantOnboardingApi(formData)}
              onSubmitted={() => {
                closeManualOnboardingModal();
                setSubmittedPage(1);
                fetchSubmittedForms(1);
              }}
            />
          </div>
        </div>
      ) : (
      <div className="WrapperArea pre-selected-consultant-page">
        <div className="WrapperBox pre-selected-consultant-box">
          {submittedDetails ? (
            <SubmittedFormDetailsModal
              details={submittedDetails}
              onClose={() => {
                setSubmittedDetails(null);
                setRequestRemark({});
                setAssetRemark({});
                setConsultantRemark("");
                setFormDataEditMode(false);
                setFormDataEdit({});
                setDocumentEditFiles({});
                setStage3Form({ agreementStatus: "pending", inductionStatus: "not_done", agreementFile: null });
              }}
              stage3Mode={isSubmittedFormsTab}
              stage3Status={currentSubmittedStage3Status}
              onRequestAction={requestSubmittedRequestAction}
              onConsultantAction={requestSubmittedConsultantAction}
              onConsultantBin={requestSubmittedConsultantBin}
              onConsultantRestore={requestSubmittedConsultantRestore}
              onStatusToggle={requestSubmittedConsultantStatusToggle}
              onFakeToggle={requestSubmittedConsultantFakeToggle}
              onAssetAction={requestSubmittedAssetAction}
              onRequestCorrectionMail={(request) =>
                openCorrectionMailModal({
                  sections: request.requestType === "BANK" ? ["bank"] : ["pan"],
                  sectionLabel: request.requestType === "BANK" ? "Bank Details" : "PAN Card",
                  remark: request.review || requestRemark[request._id] || "",
                })
              }
              onAssetCorrectionMail={(assetType, title) =>
                openCorrectionMailModal({
                  sections: assetType === "photos" ? ["photos"] : ["video"],
                  sectionLabel: title,
                  remark: assetRemark[assetType] || submittedDetails.assetReviews?.[assetType]?.remark || "",
                })
              }
              onOpenReview={openReviewModal}
              onViewConsultant={(consultantId) =>
                navigate(`${CONSULTANT_DETAIL}/${consultantId}`, {
                  state: buildPreSelectedReturnState(submittedDetails._id),
                })
              }
              onEditConsultant={(consultantId) =>
                navigate(`${CONSULTANT_EDIT}/${consultantId}`, {
                  state: buildPreSelectedReturnState(submittedDetails._id),
                })
              }
              onEditConsultantV1={(consultantId) =>
                navigate(`${CONSULTANT_EDIT_V1}/${consultantId}`, {
                  state: buildPreSelectedReturnState(submittedDetails._id),
                })
              }
              canEditFormData={currentSubmittedStage3Status === "request"}
              readOnlyConsultantReview={["deleted", "block"].includes(currentSubmittedStage3Status)}
              formDataEditMode={formDataEditMode}
              setFormDataEditMode={setFormDataEditMode}
              formDataEdit={formDataEdit}
              setFormDataEdit={setFormDataEdit}
              documentEditFiles={documentEditFiles}
              setDocumentEditFiles={setDocumentEditFiles}
              onFormDataSave={saveSubmittedConsultantData}
              requestRemark={requestRemark}
              setRequestRemark={setRequestRemark}
              assetRemark={assetRemark}
              setAssetRemark={setAssetRemark}
              consultantRemark={consultantRemark}
              setConsultantRemark={setConsultantRemark}
              actionLoading={submittedActionLoading}
            />
          ) : (
          <>
          <div
            className="TitleBox"
            style={{
              alignItems: "center",
              flexWrap: "wrap",
              columnGap: 18,
              rowGap: 12,
              minHeight: 44,
            }}
          >
            <h4 className="Title" style={{ flex: "1 1 260px" }}>INTERVIEW MANAGEMENT</h4>
            {(tab === 0 || tab === 1) && (
              <div
                className="block_user_button"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 10,
                  flex: "1 1 430px",
                  flexWrap: "wrap",
                  marginLeft: "auto",
                }}
              >
                {tab === 0 && (
                  <>
                    <input
                      ref={sheetInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      style={{ display: "none" }}
                      onChange={uploadSheetHandler}
                    />
                    <button
                      type="button"
                      onClick={downloadSheetTemplate}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: 0, minHeight: 42 }}
                    >
                      <DownloadIcon style={{ fontSize: 19 }} />
                      Download Template
                    </button>
                    <button
                      type="button"
                      onClick={() => sheetInputRef.current?.click()}
                      disabled={sheetUploading}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: 0, minHeight: 42 }}
                    >
                      <FileUploadIcon style={{ fontSize: 19 }} />
                      {sheetUploading ? "Uploading..." : "Upload Sheet"}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={tab === 1 ? openOnboardingAddModal : openAddModal}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: 0, minHeight: 42 }}
                >
                  <AddIcon style={{ fontSize: 19 }} />
                  Add Consultant
                </button>
              </div>
            )}
          </div>

          <Box sx={{ width: "100%", marginBottom: "20px" }}>
            <CustomTabs
              value={tab}
              onChange={handleTabChange}
              aria-label="pre selected consultant tabs"
            >
              <CustomTab label=" Interview Management" />
              <CustomTab label=" On-boarding Management" />
              <CustomTab label="Consultant Data Management" />
              <CustomTab label="Email Templates" />
            </CustomTabs>
          </Box>

          {tab === 0 && (
            <>
          <InterviewAnalyticsReport analytics={listAnalytics} section="interview" />
          <div
            className="FilterBox FilterArea pre-selected-filter-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))",
              gap: 12,
              alignItems: "center",
              padding: 14,
              marginBottom: 14,
              border: "1px solid #eef0f3",
              borderRadius: 6,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
            }}
          >
            <div
              className="form-group search_lg_size userSearch"
              style={{ marginBottom: 0, maxWidth: "none", gridColumn: "span 2" }}
            >
              <input
                type="search"
                className="form-control"
                placeholder="Search by name, email, phone, category or source"
                value={search || ""}
                style={{ "--color": "#FF8989" }}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="user_search_icon">{common.SEARCH_ICON}</span>
            </div>
            <div className="form-group" style={{ marginBottom: 4 }}>
              <select
                className="form-control"
                value={listFilters.status}
                onChange={(e) =>
                  setListFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="selected">Selected</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 4 }}>
              <select
                className="form-control"
                value={listFilters.interviewConduct}
                onChange={(e) =>
                  setListFilters((prev) => ({ ...prev, interviewConduct: e.target.value }))
                }
              >
                <option value="">All Conduct</option>
                {interviewConductOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 4 }}>
              <select
                className="form-control"
                value={listFilters.emailStatus}
                onChange={(e) =>
                  setListFilters((prev) => ({ ...prev, emailStatus: e.target.value }))
                }
              >
                <option value="">All Mail Status</option>
                {interviewEmailStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 4 }}>
              <select
                className="form-control"
                value={listFilters.selectionMailStatus}
                onChange={(e) =>
                  setListFilters((prev) => ({ ...prev, selectionMailStatus: e.target.value }))
                }
              >
                <option value="">All Selection Mail</option>
                {selectionMailStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                justifyContent: "flex-start",
                flexWrap: "wrap",
                gridColumn: "1 / -1",
              }}
            >
              <div className="form-group" style={{ marginBottom: 0, minWidth: 180, flex: "1 1 180px", maxWidth: 240 }}>
                <ReactDatePicker
                  selected={listFilters.startDate}
                  onChange={(date) => applyInterviewDateRange(date, listFilters.endDate)}
                  selectsStart
                  startDate={listFilters.startDate}
                  endDate={listFilters.endDate}
                  dateFormat="dd MMM yyyy"
                  className="form-control"
                  placeholderText="Interview from"
                  isClearable
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 180, flex: "1 1 180px", maxWidth: 240 }}>
                <ReactDatePicker
                  selected={listFilters.endDate}
                  onChange={(date) => applyInterviewDateRange(listFilters.startDate, date)}
                  selectsEnd
                  startDate={listFilters.startDate}
                  endDate={listFilters.endDate}
                  minDate={listFilters.startDate || undefined}
                  dateFormat="dd MMM yyyy"
                  className="form-control"
                  placeholderText="Interview to"
                  isClearable
                />
              </div>
              <button
                type="button"
                className="btn btn-light"
                style={datePresetButtonStyle(activeInterviewDatePreset === "today")}
                onClick={() => setInterviewDatePreset("today")}
              >
                Today
              </button>
              <button
                type="button"
                className="btn btn-light"
                style={datePresetButtonStyle(activeInterviewDatePreset === "last7days")}
                onClick={() => setInterviewDatePreset("last7days")}
              >
                Last 7 Days
              </button>
              <button
                type="button"
                className="btn btn-light"
                style={datePresetButtonStyle(activeInterviewDatePreset === "next7days")}
                onClick={() => setInterviewDatePreset("next7days")}
              >
                Next 7 Days
              </button>
              <button
                type="button"
                className="btn btn-light"
                style={{ border: "1px solid #d9dee5", padding: "8px 11px", fontWeight: 600 }}
                onClick={resetInterviewFilters}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 12px",
                  fontWeight: 600,
                }}
                onClick={() => setShowColumnSidebar(true)}
              >
                <ViewColumnIcon style={{ fontSize: 18 }} />
                Columns
              </button>
            </div>
          </div>

          {showColumnSidebar && (
            <>
              <div
                onClick={() => setShowColumnSidebar(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(15, 23, 42, 0.28)",
                  zIndex: 1200,
                }}
              />
              <aside
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  width: 360,
                  maxWidth: "92vw",
                  height: "100vh",
                  background: "#fff",
                  zIndex: 1201,
                  boxShadow: "-10px 0 30px rgba(15, 23, 42, 0.18)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 18px",
                    borderBottom: "1px solid #edf0f4",
                  }}
                >
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 700 }}>Table Columns</h5>
                    <div style={{ fontSize: 12, color: "#6c757d", marginTop: 3 }}>
                      {visibleInterviewColumnCount} columns visible
                    </div>
                  </div>
                  <IconButton aria-label="close columns" onClick={() => setShowColumnSidebar(false)}>
                    <CloseIcon />
                  </IconButton>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    padding: "12px 18px",
                    borderBottom: "1px solid #edf0f4",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-light"
                    style={{ flex: 1, border: "1px solid #d9dee5", fontWeight: 600 }}
                    onClick={() => setAllInterviewColumns(true)}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    style={{ flex: 1, border: "1px solid #d9dee5", fontWeight: 600 }}
                    onClick={() => setAllInterviewColumns(false)}
                  >
                    Deselect
                  </button>
                </div>
                <div style={{ padding: "8px 18px", overflowY: "auto", flex: 1 }}>
                  {interviewColumns.map((column) => (
                    <label
                      key={column.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) 24px",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 0",
                        margin: 0,
                        borderBottom: "1px solid #f1f3f5",
                        color: column.alwaysVisible ? "#6c757d" : "#1f2937",
                        fontWeight: 600,
                      }}
                    >
                      <span>{column.label}</span>
                      <input
                        type="checkbox"
                        checked={isInterviewColumnVisible(column.key)}
                        disabled={column.alwaysVisible}
                        style={{ justifySelf: "center", width: 16, height: 16 }}
                        onChange={(e) =>
                          setInterviewColumnVisibility((prev) => ({
                            ...prev,
                            [column.key]: e.target.checked,
                          }))
                        }
                      />
                    </label>
                  ))}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "14px 18px",
                    borderTop: "1px solid #edf0f4",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-light"
                    style={{ flex: 1, border: "1px solid #d9dee5", fontWeight: 600 }}
                    onClick={resetInterviewColumns}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ flex: 1, fontWeight: 600 }}
                    onClick={() => setShowColumnSidebar(false)}
                  >
                    Apply
                  </button>
                </div>
              </aside>
            </>
          )}

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  {isInterviewColumnVisible("serial") && <th>S. No.</th>}
                  {isInterviewColumnVisible("interviewDate") && <th>Interview Date</th>}
                  {isInterviewColumnVisible("source") && <th>Source</th>}
                  {isInterviewColumnVisible("name") && <th>Name</th>}
                  {isInterviewColumnVisible("contact") && <th>Contact No.</th>}
                  {isInterviewColumnVisible("email") && <th>E-Mail Id</th>}
                  {isInterviewColumnVisible("consultantType") && <th>Consultant Type</th>}
                  {isInterviewColumnVisible("interviewConduct") && <th>Interview Conduct</th>}
                  {isInterviewColumnVisible("selectionStatus") && <th>Selection Status</th>}
                  {isInterviewColumnVisible("selectionReason") && <th>Selection & Rejection Reason</th>}
                  {isInterviewColumnVisible("interviewScore") && <th>Interview Score</th>}
                  {isInterviewColumnVisible("usp") && <th>USP</th>}
                  {isInterviewColumnVisible("pricePerMin") && <th>Price/min</th>}
                  {isInterviewColumnVisible("saveDetails") && <th>Save Details</th>}
                  {isInterviewColumnVisible("mailSent") && <th>Mail Sent</th>}
                  {isInterviewColumnVisible("selectionMail") && <th>Selection Mail</th>}
                  {isInterviewColumnVisible("scheduleEmail") && <th>Schedule Email Time</th>}
                  {isInterviewColumnVisible("personalMail") && <th>Personal Mail</th>}
                  {isInterviewColumnVisible("action") && <th className="text-center">Action</th>}
                </tr>
              </thead>
              <tbody>
                {list.length > 0 ? (
                  list.map((item, index) => (
                    <tr key={item._id}>
                      {isInterviewColumnVisible("serial") && <td>{findSerialNumber(index, activePage, 10)}</td>}
                      {isInterviewColumnVisible("interviewDate") && <td>{formatDateTime(item.interviewDateTime)}</td>}
                      {isInterviewColumnVisible("source") && <td>{item.source || "---"}</td>}
                      {isInterviewColumnVisible("name") && <td>{item.name || "---"}</td>}
                      {isInterviewColumnVisible("contact") && <td>{item.phone || "---"}</td>}
                      {isInterviewColumnVisible("email") && <td>{item.email || "---"}</td>}
                      {isInterviewColumnVisible("consultantType") && <td>{item.category || "---"}</td>}
                      {isInterviewColumnVisible("interviewConduct") && (
                        <td>
                          <select
                            className="form-control"
                            style={{ minWidth: 145, textTransform: "capitalize" }}
                            value={getInterviewDraftValue(item, "interviewConduct") || "pending"}
                            onChange={(e) =>
                              updateInterviewDetailDraft(item, "interviewConduct", e.target.value)
                            }
                          >
                            {interviewConductOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      {isInterviewColumnVisible("selectionStatus") && (
                        <td>
                          {item.emailStatus === "sent" ? (
                            <select
                              className="form-control"
                              style={{ minWidth: 120, textTransform: "capitalize" }}
                              value={item.interviewStatus || "pending"}
                              onChange={(e) => interviewStatusHandler(item, e.target.value)}
                            >
                              {interviewStatusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            "---"
                          )}
                        </td>
                      )}
                      {isInterviewColumnVisible("selectionReason") && (
                        <td>
                          <textarea
                            className="form-control"
                            value={getInterviewDraftValue(item, "selectionRejectionReason")}
                            rows={2}
                            style={{ minWidth: 220, resize: "vertical" }}
                            placeholder="Write remark"
                            onChange={(e) =>
                              updateInterviewDetailDraft(item, "selectionRejectionReason", e.target.value)
                            }
                          />
                        </td>
                      )}
                      {isInterviewColumnVisible("interviewScore") && (
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            max="100"
                            value={getInterviewDraftValue(item, "interviewScore")}
                            style={{ minWidth: 90 }}
                            onChange={(e) =>
                              updateInterviewDetailDraft(item, "interviewScore", e.target.value)
                            }
                          />
                        </td>
                      )}
                      {isInterviewColumnVisible("usp") && (
                        <td>
                          <textarea
                            className="form-control"
                            value={getInterviewDraftValue(item, "usp")}
                            rows={2}
                            style={{ minWidth: 180, resize: "vertical" }}
                            placeholder="USP"
                            onChange={(e) => updateInterviewDetailDraft(item, "usp", e.target.value)}
                          />
                        </td>
                      )}
                      {isInterviewColumnVisible("pricePerMin") && (
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            value={getInterviewDraftValue(item, "pricePerMin")}
                            style={{ minWidth: 95 }}
                            onChange={(e) =>
                              updateInterviewDetailDraft(item, "pricePerMin", e.target.value)
                            }
                          />
                        </td>
                      )}
                      {isInterviewColumnVisible("saveDetails") && (
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              whiteSpace: "nowrap",
                              fontWeight: 600,
                              padding: "6px 10px",
                              background: "#FF8989",
                              borderColor: "#FF8989",
                              color: "#fff",
                            }}
                            onClick={() => saveInterviewDetails(item)}
                          >
                            Save Details
                          </button>
                        </td>
                      )}
                      {isInterviewColumnVisible("mailSent") && (
                        <td>
                          {isManualCreatedConsultant(item) ? (
                            <ManualCreatedBadge />
                          ) : (
                            <>
                              <span
                                style={{
                                  textTransform: "capitalize",
                                  fontWeight: 600,
                                  color:
                                    item.emailStatus === "sent"
                                      ? "#198754"
                                      : item.emailStatus === "failed"
                                      ? "#dc3545"
                                      : "#6c757d",
                                }}
                              >
                                {item.emailStatus || "---"}
                              </span>
                              {item.sentAt ? <div>Interview Mail: {formatDateTime(item.sentAt)}</div> : null}
                              {item.lastError ? (
                                <div
                                  style={{ color: "#dc3545", fontSize: 12, maxWidth: 220 }}
                                  title={item.lastError}
                                >
                                  {/daily user sending limit exceeded/i.test(item.lastError)
                                    ? "Gmail daily limit exceeded. Retry scheduled."
                                    : item.lastError.length > 90
                                    ? `${item.lastError.slice(0, 90)}...`
                                    : item.lastError}
                                </div>
                              ) : null}
                            </>
                          )}
                        </td>
                      )}
                      {isInterviewColumnVisible("selectionMail") && (
                        <td>
                          {isManualCreatedConsultant(item) ? (
                            <ManualCreatedBadge />
                          ) : item.interviewStatusMailSentAt ? (
                            <div style={{ color: "#198754", fontWeight: 600 }}>
                              Sent
                            </div>
                          ) : (
                            <div style={{ color: "#6c757d", fontWeight: 600 }}>Not Sent</div>
                          )}
                          {!isManualCreatedConsultant(item) && item.interviewStatusMailSentAt ? (
                            <div>{formatDateTime(item.interviewStatusMailSentAt)}</div>
                          ) : null}
                        </td>
                      )}
                      {isInterviewColumnVisible("scheduleEmail") && (
                        <td>{isManualCreatedConsultant(item) ? <ManualCreatedBadge /> : formatDateTime(item.scheduleEmailAt)}</td>
                      )}
                      {isInterviewColumnVisible("personalMail") && (
                        <td>
                        {item.personalizedEmailSentAt ? (
                          <div>
                            <div style={{ color: "#198754", fontWeight: 600 }}>Shared</div>
                            <div>{formatDateTime(item.personalizedEmailSentAt)}</div>
                            {item.personalizedEmailSubject ? (
                              <div title={item.personalizedEmailSubject}>
                                {String(item.personalizedEmailSubject).slice(0, 28)}
                                {String(item.personalizedEmailSubject).length > 28 ? "..." : ""}
                              </div>
                            ) : null}
                            {item.personalizedEmailCount > 1 ? (
                              <div>Total: {item.personalizedEmailCount}</div>
                            ) : null}
                          </div>
                        ) : (
                          "---"
                        )}
                        </td>
                      )}
                      {isInterviewColumnVisible("action") && (
                        <td>
                        <div
                          className="action_button"
                          style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "nowrap" }}
                        >
                          <IconButton
                            aria-label="edit"
                            className="px-2 py-0"
                            onClick={() => openEditModal(item)}
                            disabled={item.emailStatus === "sent"}
                            title={item.emailStatus === "sent" ? "Cannot edit after email sent" : "Edit"}
                          >
                            <EditIcon className="edit_icon" />
                          </IconButton>
                          <IconButton
                            aria-label="send personal mail"
                            className="px-2 py-0"
                            onClick={() => openPersonalMailModal(item)}
                            title="Send Personal Mail"
                          >
                            <MailOutlineIcon className="edit_icon" />
                          </IconButton>
                          <IconButton
                            aria-label="reschedule interview mail"
                            className="px-2 py-0"
                            onClick={() => openRescheduleModal(item)}
                            title="Reschedule Interview Mail"
                          >
                            <ScheduleSendIcon className="edit_icon" />
                          </IconButton>
                          <IconButton
                            aria-label="activity logs"
                            className="px-2 py-0"
                            onClick={() => setLogsData(item)}
                            title="Activity Logs"
                          >
                            <HistoryIcon className="edit_icon" />
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            className="px-2 py-0"
                            onClick={() => requestDeletePreSelectedConsultant(item)}
                            title="Delete"
                          >
                            <DeleteIcon className="edit_icon" style={{ color: "#dc3545" }} />
                          </IconButton>
                        </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <NotFound
                    cols={visibleInterviewColumnCount}
                    msg="Pre selected consultant not found!"
                    icon={NOT_FOUND}
                    loader={loader}
                  />
                )}
              </tbody>
            </table>
          </div>

          {total > 10 ? (
            <div className="Pagination">
              <Pagination
                activePage={activePage}
                previousLabel="previous"
                nextLabel="next"
                itemsCountPerPage={10}
                totalItemsCount={total}
                pageRangeDisplayed={5}
                onChange={setActivePage}
              />
            </div>
          ) : null}
            </>
          )}

          {isSubmittedFormsTab && (
            <>
          {isOnboardingTab && (
            <InterviewAnalyticsReport analytics={listAnalytics} section="onboarding" />
          )}
          {isConsultantDataTab && (
            <div
              style={{
                marginBottom: 12,
                color: "#343a40",
                fontWeight: 600,
                background: "#fff",
                border: "1px solid #eef0f3",
                borderRadius: 6,
                padding: "10px 12px",
              }}
            >
              Final approved consultant profile data only. This view stays read-only like Consultant Management listing.
            </div>
          )}
          {isConsultantDataTab && (
            <Stage3StatusSelector
              value={submittedFilters.stage3Status}
              options={consultantDataStatusOptions}
              onChange={(value) =>
                setSubmittedFilters((prev) => ({ ...prev, stage3Status: value }))
              }
            />
          )}
          <div
            className="FilterBox FilterArea pre-selected-filter-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))",
              columnGap: 16,
              rowGap: 14,
              alignItems: "center",
              padding: "18px 20px",
              marginBottom: 18,
              border: "1px solid #eef0f3",
              borderRadius: 6,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
            }}
          >
            <div
              className="form-group search_lg_size userSearch"
              style={{ marginBottom: 4, maxWidth: "none", gridColumn: "span 2" }}
            >
              <input
                type="search"
                className="form-control"
                placeholder="Search by name, email, phone, category, source or ID"
                value={submittedFilters.search || ""}
                style={{ "--color": "#FF8989" }}
                onChange={(e) =>
                  setSubmittedFilters((prev) => ({ ...prev, search: e.target.value }))
                }
              />
              <span className="user_search_icon">{common.SEARCH_ICON}</span>
            </div>
            <div className="form-group" style={{ marginBottom: 4, minWidth: 180 }}>
              <label className="my-1 text-dark" style={{ fontWeight: 600, fontSize: 12 }}>
                {isConsultantDataTab ? "Registration From" : "Form Submitted From"}
              </label>
              <ReactDatePicker
                selected={submittedFilters.startDate}
                onChange={(date) => applySubmittedDateRange(date, submittedFilters.endDate)}
                selectsStart
                startDate={submittedFilters.startDate}
                endDate={submittedFilters.endDate}
                maxDate={submittedDateMax}
                dateFormat="dd MMM yyyy"
                className="form-control"
                placeholderText={isConsultantDataTab ? "Registration from" : "Form submitted from"}
                isClearable
              />
            </div>
            <div className="form-group" style={{ marginBottom: 4, minWidth: 180 }}>
              <label className="my-1 text-dark" style={{ fontWeight: 600, fontSize: 12 }}>
                {isConsultantDataTab ? "Registration To" : "Form Submitted To"}
              </label>
              <ReactDatePicker
                selected={submittedFilters.endDate}
                onChange={(date) => applySubmittedDateRange(submittedFilters.startDate, date)}
                selectsEnd
                startDate={submittedFilters.startDate}
                endDate={submittedFilters.endDate}
                minDate={submittedFilters.startDate || undefined}
                maxDate={submittedDateMax}
                dateFormat="dd MMM yyyy"
                className="form-control"
                placeholderText={isConsultantDataTab ? "Registration to" : "Form submitted to"}
                isClearable
              />
            </div>
            {isOnboardingTab && (
              <>
                <div className="form-group" style={{ marginBottom: 4 }}>
                  <select
                    className="form-control"
                    value={submittedFilters.interviewStatus}
                    onChange={(e) => setSubmittedFilters((prev) => ({ ...prev, interviewStatus: e.target.value }))}
                  >
                    <option value="">All Interview Status</option>
                    {interviewStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 4 }}>
                  <select
                    className="form-control"
                    value={submittedFilters.selectionMailStatus}
                    onChange={(e) => setSubmittedFilters((prev) => ({ ...prev, selectionMailStatus: e.target.value }))}
                  >
                    <option value="">All Selection Mail</option>
                    {selectionMailStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 4 }}>
                  <select
                    className="form-control"
                    value={submittedFilters.selectionCallDone}
                    onChange={(e) => setSubmittedFilters((prev) => ({ ...prev, selectionCallDone: e.target.value }))}
                  >
                    <option value="">All Selection Call</option>
                    {onboardingYesNoOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {[
                  ["photosStatus", "All Photo Status"],
                  ["profileStatus", "All Profile Status"],
                  ["panStatus", "All Document/PAN"],
                  ["bankStatus", "All Bank Details"],
                ].map(([key, label]) => (
                  <div className="form-group" style={{ marginBottom: 4 }} key={key}>
                    <select
                      className="form-control"
                      value={submittedFilters[key]}
                      onChange={(e) => setSubmittedFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                    >
                      <option value="">{label}</option>
                      {onboardingStatusOptions.filter((option) => option.value !== "na").map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </>
            )}
            {isOnboardingTab && (
              <>
                {[
                  ["idCreatedStatus", "All ID Created"],
                  ["idLiveStatus", "All ID Live"],
                  ["agreementSignStatus", "All Agreement Sign"],
                  ["agreementUploadStatus", "All Agreement Upload"],
                  ["inductionStatus", "All Induction"],
                ].map(([key, label]) => (
                  <div className="form-group" style={{ marginBottom: 4 }} key={key}>
                    <select
                      className="form-control"
                      value={submittedFilters[key]}
                      onChange={(e) => setSubmittedFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                    >
                      <option value="">{label}</option>
                      {onboardingYesNoOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
                <div className="form-group" style={{ marginBottom: 4 }}>
                  <select
                    className="form-control"
                    value={submittedFilters.finalStatus}
                    onChange={(e) => setSubmittedFilters((prev) => ({ ...prev, finalStatus: e.target.value }))}
                  >
                    <option value="">All Final Status</option>
                    {onboardingFinalStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-start",
                gridColumn: "span 2",
              }}
            >
              <button type="button" className="btn btn-light" style={datePresetButtonStyle(activeSubmittedDatePreset === "today")} onClick={() => setSubmittedDatePreset("today")}>Today</button>
              <button type="button" className="btn btn-light" style={datePresetButtonStyle(activeSubmittedDatePreset === "last7days")} onClick={() => setSubmittedDatePreset("last7days")}>Last 7 Days</button>
              <button type="button" className="btn btn-light" style={{ border: "1px solid #d9dee5", fontWeight: 600, minWidth: 56 }} onClick={resetSubmittedFilters}>Clear</button>
              <button type="button" className="btn btn-primary" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, minWidth: 102 }} onClick={() => setShowSubmittedColumnSidebar(true)}>
                <ViewColumnIcon style={{ fontSize: 18 }} />
                Columns
              </button>
            </div>
          </div>

          {showSubmittedColumnSidebar && (
            <>
              <div onClick={() => setShowSubmittedColumnSidebar(false)} style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.28)", zIndex: 1200 }} />
              <aside style={{ position: "fixed", top: 0, right: 0, width: 380, maxWidth: "92vw", height: "100vh", background: "#fff", zIndex: 1201, boxShadow: "-10px 0 30px rgba(15, 23, 42, 0.18)", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderBottom: "1px solid #edf0f4" }}>
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 700 }}>
                      {isConsultantDataTab ? "Consultant Data Columns" : "Onboarding Columns"}
                    </h5>
                    <div style={{ fontSize: 12, color: "#6c757d", marginTop: 3 }}>{visibleSubmittedColumnCount} columns visible</div>
                  </div>
                  <IconButton aria-label="close columns" onClick={() => setShowSubmittedColumnSidebar(false)}>
                    <CloseIcon />
                  </IconButton>
                </div>
                <div style={{ display: "flex", gap: 8, padding: "12px 18px", borderBottom: "1px solid #edf0f4" }}>
                  <button type="button" className="btn btn-light" style={{ flex: 1, border: "1px solid #d9dee5", fontWeight: 600 }} onClick={() => setAllSubmittedColumns(true)}>Select All</button>
                  <button type="button" className="btn btn-light" style={{ flex: 1, border: "1px solid #d9dee5", fontWeight: 600 }} onClick={() => setAllSubmittedColumns(false)}>Deselect</button>
                </div>
                <div style={{ padding: "8px 18px", overflowY: "auto", flex: 1 }}>
                  {activeSubmittedColumns.map((column) => (
                    <label key={column.key} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 24px", alignItems: "center", gap: 12, padding: "12px 0", margin: 0, borderBottom: "1px solid #f1f3f5", color: column.alwaysVisible ? "#6c757d" : "#1f2937", fontWeight: 600 }}>
                      <span>{column.label}</span>
                      <input type="checkbox" checked={isSubmittedColumnVisible(column.key)} disabled={column.alwaysVisible} style={{ justifySelf: "center", width: 16, height: 16 }} onChange={(e) => setSubmittedColumnVisibility((prev) => ({ ...prev, [column.key]: e.target.checked }))} />
                    </label>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, padding: "14px 18px", borderTop: "1px solid #edf0f4" }}>
                  <button type="button" className="btn btn-light" style={{ flex: 1, border: "1px solid #d9dee5", fontWeight: 600 }} onClick={resetSubmittedColumns}>Reset</button>
                  <button type="button" className="btn btn-primary" style={{ flex: 1, fontWeight: 600 }} onClick={() => setShowSubmittedColumnSidebar(false)}>Apply</button>
                </div>
              </aside>
            </>
          )}

          {isOnboardingTab && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              <div style={{ fontWeight: 600 }}>
                {selectedSubmittedIds.length} consultant selected
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ background: "#FF8989", borderColor: "#FF8989", color: "#fff", fontWeight: 600 }}
                  disabled={!selectedSubmittedIds.length}
                  onClick={() => {
                    setSubmittedCommunicationForm((prev) => ({ ...prev, channel: "email" }));
                    setSubmittedCommunicationModal(true);
                  }}
                >
                  Send Email
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ background: "#FF8989", borderColor: "#FF8989", color: "#fff", fontWeight: 600 }}
                  disabled={!selectedSubmittedIds.length}
                  onClick={() => {
                    setSubmittedCommunicationForm((prev) => ({ ...prev, channel: "whatsapp" }));
                    setSubmittedCommunicationModal(true);
                  }}
                >
                  Send WhatsApp
                </button>
              </div>
            </div>
          )}

          <style>
            {`
              .submitted-onboarding-table table th,
              .submitted-onboarding-table table td {
                padding: 13px 14px;
                vertical-align: middle;
              }

              .submitted-onboarding-table table thead th {
                font-size: 13px;
                line-height: 1.3;
                white-space: nowrap;
              }

              .submitted-onboarding-table table tbody td {
                font-size: 14px;
                line-height: 1.45;
              }

              .submitted-onboarding-table table tbody tr {
                min-height: 58px;
              }
            `}
          </style>
          <div className="content-table submitted-onboarding-table">
            <table>
              <thead>
                <tr>
                  {isOnboardingTab && isSubmittedColumnVisible("select") && <th>
                    <input
                      type="checkbox"
                      checked={
                        submittedForms.length > 0 &&
                        submittedForms.every((item) => selectedSubmittedIds.includes(item._id))
                      }
                      onChange={toggleAllSubmittedSelection}
                    />
                  </th>}
                  {isSubmittedColumnVisible("serial") && <th>S. No.</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("date") && <th>Date</th>}
                  {isConsultantDataTab && isSubmittedColumnVisible("profile") && <th>Profile</th>}
                  {isConsultantDataTab && isSubmittedColumnVisible("consultantId") && <th>ID</th>}
                  {isSubmittedColumnVisible("candidateName") && <th>{isConsultantDataTab ? "Consultant Name" : "Candidate Name"}</th>}
                  {isConsultantDataTab && isSubmittedColumnVisible("userName") && <th>User Name</th>}
                  {isSubmittedColumnVisible("contact") && <th>Contact No.</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("email") && <th>E-Mail Id</th>}
                  {isConsultantDataTab && isSubmittedColumnVisible("consultantType") && <th>Type</th>}
                  {isConsultantDataTab && isSubmittedColumnVisible("gender") && <th>Gender</th>}
                  {isConsultantDataTab && isSubmittedColumnVisible("registrationDate") && <th>Registration Date</th>}
                  {isConsultantDataTab && isSubmittedColumnVisible("profileComplete") && <th>Profile Complete</th>}
                  {isConsultantDataTab && isSubmittedColumnVisible("statusDate") && <th>Status Date</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("selectionMail") && <th>Selection Mail</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("selectionCallDate") && <th>Selection Call Date</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("selectionCallDone") && <th>Selection Call Done</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("profilePhoto") && <th>Profile Photo</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("galleryPhoto") && <th>Gallery Photo</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("profileDetail") && <th>Profile Detail</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("professionalDetail") && <th>Professional Detail</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("addressDetails") && <th>Address Details</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("personalPreferences") && <th>Personal Preferences</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("additionalQuestions") && <th>Additional Questions</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("document") && <th>Document</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("bankDetails") && <th>Bank Details</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("idCreatedDate") && <th>ID Created Date</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("idCreated") && <th>ID Created</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("idLiveDate") && <th>ID Live Date</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("idLive") && <th>ID Live</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("agreementDate") && <th>Agreement Date</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("agreementSign") && <th>Agreement Sign</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("agreementUpload") && <th>Agreement Upload</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("inductionDate") && <th>Induction Date</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("induction") && <th>Induction</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("finalStatus") && <th>Final Status</th>}
                  {isOnboardingTab && isSubmittedColumnVisible("save") && <th>Save</th>}
                  {isSubmittedColumnVisible("action") && <th className="text-center">Action</th>}
                </tr>
              </thead>
              <tbody>
                {submittedForms.length > 0 ? (
                  submittedForms.map((item, index) => {
                    const formData = item.formData || {};
                    const consultant = item.consultantId || {};
                    const preSelected = item.preSelectedConsultantId || {};
                    const submittedAt = preSelected.selectedFormSubmittedAt || item.createdAt;
                    const photosStatus = reviewToOnboardingStatus(item.assetReviews?.photos);
                    const panStatus = requestTypeToOnboardingStatus(item.requests || [], "PANCARD");
                    const bankStatus = requestTypeToOnboardingStatus(item.requests || [], "BANK");
                    const profileDetailsStatus = consultantReviewToOnboardingStatus(consultant);
                    const selectControl = (key, fallback = "pending", options = onboardingStatusOptions, useSavedValue = true) => (
                      <select
                        className="form-control"
                        style={{
                          minWidth: 132,
                          height: 36,
                          fontSize: 14,
                          fontWeight: 600,
                          borderColor: "#d4dae3",
                          borderRadius: 4,
                        }}
                        value={
                          useSavedValue
                            ? getSubmittedDraftValue(item, key, fallback)
                            : submittedRowDrafts[item._id]?.[key] ?? fallback
                        }
                        onChange={(e) => updateSubmittedRowDraft(item, key, e.target.value)}
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    );
                    const dateInput = (key, fallback = "") => (
                      <input
                        type="date"
                        className="form-control"
                        style={{
                          minWidth: 150,
                          height: 36,
                          fontSize: 14,
                          borderColor: "#d4dae3",
                          borderRadius: 4,
                        }}
                        value={getSubmittedDraftValue(item, key, formatInputDate(fallback))}
                        onChange={(e) => updateSubmittedRowDraft(item, key, e.target.value)}
                      />
                    );
                    const statusBadge = (status = "pending") => {
                      const option = onboardingStatusOptions.find((itemOption) => itemOption.value === status);
                      const colorMap = {
                        accept: { color: "#198754", background: "#eaf7ef", borderColor: "#b9e2c8" },
                        reject: { color: "#dc3545", background: "#fff1f1", borderColor: "#f3b8bf" },
                        pending: { color: "#856404", background: "#fff8df", borderColor: "#f1df9a" },
                        na: { color: "#6c757d", background: "#f1f3f5", borderColor: "#d7dce1" },
                      };
                      const style = colorMap[status] || colorMap.pending;
                      return (
                        <span
                          style={{
                            ...style,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 88,
                            minHeight: 30,
                            border: `1px solid ${style.borderColor}`,
                            borderRadius: 4,
                            padding: "4px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {option?.label || "Pending"}
                        </span>
                      );
                    };
                    const agreementUploadControl = () => (
                      <div style={{ minWidth: 220 }}>
                        <input
                          type="file"
                          className="form-control"
                          style={{
                            minWidth: 220,
                            height: 36,
                            fontSize: 14,
                            borderColor: "#d4dae3",
                            borderRadius: 4,
                          }}
                          onChange={(e) =>
                            updateSubmittedRowFile(item, "agreement", e.target.files?.[0] || null)
                          }
                        />
                        <div style={{ marginTop: 5, fontSize: 13, color: "#6c757d" }}>
                          {submittedRowFiles[item._id]?.agreement?.name ||
                            (item.stage3Controls?.agreementFile
                              ? renderDocumentLink(item.stage3Controls.agreementFile, "Uploaded")
                              : "No file uploaded")}
                        </div>
                      </div>
                    );
                    const readonlyCell = (value, width = 150) => (
                      <div
                        style={{
                          minWidth: width,
                          maxWidth: width + 90,
                          fontWeight: 600,
                          fontSize: 14,
                          color: "#111827",
                          whiteSpace: "normal",
                          lineHeight: 1.35,
                        }}
                      >
                        {value || "---"}
                      </div>
                    );
                    const consultantName =
                      formData.name || preSelected.name || consultant.name || consultant.accountName;
                    const consultantUserName =
                      consultant.accountName || consultant.userName || consultant.username || formData.userName || formData.username;
                    const consultantPhone =
                      consultant.mobile || consultant.details?.phone || formData.primaryNumber || preSelected.phone;
                    const consultantProfileImage =
                      consultant.profileImage || item.documents?.profileImage || formData.profileImage || "";
                    const consultantStatusDate =
                      consultant.statusUpdatedAt ||
                      consultant.approvalActionAt ||
                      consultant.approvedAt ||
                      consultant.disapprovedAt ||
                      consultant.updatedAt ||
                      item.updatedAt;
                    const consultantProfilePercent =
                      consultant.profileComplete ||
                      consultant.profileCompletion ||
                      consultant.profileCompletePercentage ||
                      consultant.profilePercentage ||
                      consultant.profileCompletedPercentage ||
                      "";
                    const rowConsultantId = getConsultantRecordId(item);
                    const profileCell = () => (
                      <div
                        style={{
                          width: 46,
                          height: 46,
                          borderRadius: "50%",
                          overflow: "hidden",
                          background: "#f2f4f7",
                          border: "1px solid #d9dee5",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          color: "#343a40",
                        }}
                      >
                        {consultantProfileImage ? (
                          <img
                            src={consultantProfileImage}
                            alt={consultantName || "Consultant"}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          (consultantName || consultantUserName || "C").charAt(0).toUpperCase()
                        )}
                      </div>
                    );
                    return (
                      <tr key={item._id}>
                        {isOnboardingTab && isSubmittedColumnVisible("select") && <td>
                          <input
                            type="checkbox"
                            checked={selectedSubmittedIds.includes(item._id)}
                            onChange={() => toggleSubmittedSelection(item._id)}
                          />
                        </td>}
                        {isSubmittedColumnVisible("serial") && <td>{findSerialNumber(index, submittedPage, 10)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("date") && <td>{readonlyCell(formatDateTime(submittedAt), 150)}</td>}
                        {isConsultantDataTab && isSubmittedColumnVisible("profile") && <td>{profileCell()}</td>}
                        {isConsultantDataTab && isSubmittedColumnVisible("consultantId") && <td>{readonlyCell(consultant.userId || formData.consultantUserId || consultant._id, 140)}</td>}
                        {isSubmittedColumnVisible("candidateName") && <td>{readonlyCell(consultantName, 190)}</td>}
                        {isConsultantDataTab && isSubmittedColumnVisible("userName") && <td>{readonlyCell(consultantUserName, 180)}</td>}
                        {isSubmittedColumnVisible("contact") && <td>{readonlyCell(consultantPhone, 140)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("email") && <td>{readonlyCell(consultant.email || formData.email || preSelected.email, 210)}</td>}
                        {isConsultantDataTab && isSubmittedColumnVisible("consultantType") && <td>{readonlyCell(consultant.consType || formData.consultantType || preSelected.category, 130)}</td>}
                        {isConsultantDataTab && isSubmittedColumnVisible("gender") && <td>{readonlyCell(consultant.gender || consultant.details?.gender || formData.gender, 110)}</td>}
                        {isConsultantDataTab && isSubmittedColumnVisible("registrationDate") && <td>{readonlyCell(formatDateTime(consultant.created_at || consultant.createdAt || item.createdAt), 155)}</td>}
                        {isConsultantDataTab && isSubmittedColumnVisible("profileComplete") && <td>{readonlyCell(consultantProfilePercent ? `${consultantProfilePercent}${String(consultantProfilePercent).includes("%") ? "" : "%"}` : "---", 135)}</td>}
                        {isConsultantDataTab && isSubmittedColumnVisible("statusDate") && <td>{readonlyCell(formatDateTime(consultantStatusDate), 155)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("selectionMail") && <td>{selectControl("selectionMailSent", preSelected.interviewStatusMailSentAt ? "sent" : "not_sent", onboardingSentOptions)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("selectionCallDate") && <td>{dateInput("selectionCallDate")}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("selectionCallDone") && <td>{selectControl("selectionCallDone", "pending", onboardingYesNoOptions)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("profilePhoto") && <td>{statusBadge(photosStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("galleryPhoto") && <td>{statusBadge(photosStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("profileDetail") && <td>{statusBadge(profileDetailsStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("professionalDetail") && <td>{statusBadge(profileDetailsStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("addressDetails") && <td>{statusBadge(profileDetailsStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("personalPreferences") && <td>{statusBadge(profileDetailsStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("additionalQuestions") && <td>{statusBadge(profileDetailsStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("document") && <td>{statusBadge(panStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("bankDetails") && <td>{statusBadge(bankStatus)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("idCreatedDate") && <td>{dateInput("idCreatedDate")}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("idCreated") && <td>{selectControl("idCreatedStatus", consultant.userId ? "yes" : "pending", onboardingYesNoOptions)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("idLiveDate") && <td>{dateInput("idLiveDate", getStage3FieldValue("liveDate", item))}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("idLive") && <td>{selectControl("idLiveStatus", getStage3FieldValue("liveDate", item) ? "yes" : "pending", onboardingYesNoOptions)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("agreementDate") && <td>{dateInput("agreementDate")}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("agreementSign") && <td>{selectControl("agreementSignStatus", item.stage3Controls?.agreementStatus === "done" ? "yes" : "pending", onboardingYesNoOptions)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("agreementUpload") && <td>{agreementUploadControl()}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("inductionDate") && <td>{dateInput("inductionDate")}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("induction") && <td>{selectControl("inductionStatus", item.stage3Controls?.inductionStatus === "done" ? "yes" : "pending", onboardingYesNoOptions)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("finalStatus") && <td>{selectControl("finalStatus", "pending", onboardingFinalStatusOptions)}</td>}
                        {isOnboardingTab && isSubmittedColumnVisible("save") && <td>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              background: "#FF8989",
                              borderColor: "#FF8989",
                              color: "#fff",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              minWidth: 78,
                            }}
                            disabled={submittedActionLoading}
                            onClick={() => saveSubmittedOnboardingRow(item)}
                          >
                            Save
                          </button>
                        </td>}
                        {isSubmittedColumnVisible("action") && <td>
                          <div
                            className="action_button"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 8,
                              minWidth: isConsultantDataTab ? 220 : 70,
                              flexWrap: "wrap",
                            }}
                          >
                            {isConsultantDataTab ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  style={{ fontWeight: 600, minWidth: 72 }}
                                  disabled={!rowConsultantId}
                                  onClick={() => openSubmittedDetails(item)}
                                >
                                  Profile
                                </button>
                                {currentSubmittedStage3Status === "active" && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      style={{ fontWeight: 600, minWidth: 64 }}
                                      disabled={submittedActionLoading || !rowConsultantId}
                                      onClick={() => requestSubmittedConsultantStatusToggle(rowConsultantId, true)}
                                    >
                                      Block
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      style={{ fontWeight: 600, minWidth: 64 }}
                                      disabled={submittedActionLoading || !rowConsultantId}
                                      onClick={() => {
                                        setConsultantRemark("");
                                        requestSubmittedConsultantBin(rowConsultantId);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                                {currentSubmittedStage3Status === "block" && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-success"
                                      style={{ color: "#fff", fontWeight: 600, minWidth: 64 }}
                                      disabled={submittedActionLoading || !rowConsultantId}
                                      onClick={() => requestSubmittedConsultantStatusToggle(rowConsultantId, false)}
                                    >
                                      Active
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      style={{ fontWeight: 600, minWidth: 64 }}
                                      disabled={submittedActionLoading || !rowConsultantId}
                                      onClick={() => {
                                        setConsultantRemark("");
                                        requestSubmittedConsultantBin(rowConsultantId);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                                {currentSubmittedStage3Status === "deleted" && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ fontWeight: 600, minWidth: 78 }}
                                    disabled={submittedActionLoading || !rowConsultantId}
                                    onClick={() => requestSubmittedConsultantRestore(rowConsultantId)}
                                  >
                                    Undelete
                                  </button>
                                )}
                              </>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  openSubmittedDetails(item);
                                }}
                              >
                                View
                              </button>
                            )}
                          </div>
                        </td>}
                      </tr>
                    );
                  })
                ) : (
                  <NotFound
                    cols={visibleSubmittedColumnCount}
                    msg={isConsultantDataTab ? "Final consultant records not found!" : "Submitted form records not found!"}
                    icon={NOT_FOUND}
                    loader={loader}
                  />
                )}
              </tbody>
            </table>
          </div>

          {submittedTotal > 10 ? (
            <div className="Pagination">
              <Pagination
                activePage={submittedPage}
                previousLabel="previous"
                nextLabel="next"
                itemsCountPerPage={10}
                totalItemsCount={submittedTotal}
                pageRangeDisplayed={5}
                onChange={setSubmittedPage}
              />
            </div>
          ) : null}
            </>
          )}

          {tab === 3 && (
            <EmailTemplateEditor
              templates={emailTemplates}
              activeTemplateType={activeTemplateType}
              setActiveTemplateType={setActiveTemplateType}
              onChange={updateTemplateField}
              onSave={saveEmailTemplate}
              saving={false}
              buttonref={templateButtonRef}
            />
          )}
          </>
          )}
        </div>
      </div>
      )}

      {modal && (
        <PreSelectedConsultantModal
          show={modal}
          onClose={closeModal}
          selectedData={selectedData}
          fetchList={() => {
            fetchList(1);
            if (isSubmittedFormsTab) fetchSubmittedForms(1);
          }}
          mode={addModalMode}
        />
      )}
      {personalMailData && (
        <PersonalizedEmailModal
          show={Boolean(personalMailData)}
          consultant={personalMailData}
          form={personalMailForm}
          error={personalMailError}
          onClose={closePersonalMailModal}
          onChange={updatePersonalMailField}
          onConfirm={confirmPersonalMailHandler}
        />
      )}
      {rescheduleData && (
        <InterviewRescheduleModal
          show={Boolean(rescheduleData)}
          consultant={rescheduleData}
          form={rescheduleForm}
          error={rescheduleError}
          onClose={closeRescheduleModal}
          onChange={updateRescheduleField}
          onSave={saveRescheduleMail}
        />
      )}
      {logsData && (
        <InterviewLogsModal
          show={Boolean(logsData)}
          consultant={logsData}
          onClose={() => setLogsData(null)}
        />
      )}
      {submittedCommunicationModal && (
        <SubmittedCommunicationModal
          show={submittedCommunicationModal}
          selectedCount={selectedSubmittedIds.length}
          form={submittedCommunicationForm}
          error={submittedCommunicationError}
          onClose={() => setSubmittedCommunicationModal(false)}
          onChange={updateSubmittedCommunicationField}
          onSend={sendSubmittedCommunication}
        />
      )}
      {correctionMailModal && (
        <CorrectionMailModal
          show={correctionMailModal}
          form={correctionMailForm}
          error={correctionMailError}
          onClose={() => setCorrectionMailModal(false)}
          onChange={updateCorrectionMailField}
          onSend={sendCorrectionMail}
        />
      )}
      {selectedStatusData && (
        <ModalBox
          status={Boolean(selectedStatusData)}
          onClose={() => setSelectedStatusData(null)}
          title="Selected Consultant Details"
          size="sm"
          closeIcon
          custom
          customizedFooter={
            <div className="model_custom_footer">
              <button className="btn_size" ref={buttonref} onClick={saveSelectedStatus}>
                Save
              </button>
            </div>
          }
        >
          <div>
            <div className="form-group mb-2">
              <Input
                type="text"
                value={statusForm.selectionName || ""}
                onChange={(e) =>
                  setStatusForm((prev) => ({ ...prev, selectionName: e.target.value }))
                }
                className="form-control"
                label="Name"
                required
                placeholder="Name"
                error={statusError.selectionName}
              />
            </div>
            <div className="form-group mb-2">
              <Input
                type="number"
                value={statusForm.selectionPrice || ""}
                onChange={(e) =>
                  setStatusForm((prev) => ({ ...prev, selectionPrice: e.target.value }))
                }
                className="form-control"
                label="Price"
                required
                placeholder="Price"
                error={statusError.selectionPrice}
              />
            </div>
          </div>
        </ModalBox>
      )}
      {confirmStatusData && (
        <ModalBox
          status={Boolean(confirmStatusData)}
          onClose={() => setConfirmStatusData(null)}
          title="Confirm Interview Status"
          description={
            confirmStatusData.status === "selected"
              ? `Do you want to mark this consultant as Selected? Selected email will be sent to ${confirmStatusData.item?.email}.`
              : confirmStatusData.status === "rejected"
              ? `Do you want to mark this consultant as Rejected? Rejection email will be sent to ${confirmStatusData.item?.email}.`
              : "Do you want to mark this consultant as Pending? No email will be sent."
          }
          action={confirmStatusChange}
          buttonref={buttonref}
        />
      )}
      {confirmDeleteData && (
        <ModalBox
          status={Boolean(confirmDeleteData)}
          onClose={() => setConfirmDeleteData(null)}
          title="Confirm Delete"
          description={`Are you sure you want to delete ${confirmDeleteData.name || "this entry"}?`}
          action={deletePreSelectedConsultant}
        />
      )}
      {confirmPersonalMail && (
        <ModalBox
          status={confirmPersonalMail}
          onClose={() => setConfirmPersonalMail(false)}
          title="Confirm Personal Mail"
          description={`Do you want to send this email to ${personalMailData?.email}?`}
          action={sendPersonalizedMail}
          actionLoading={personalMailSending}
        />
      )}
      {reviewModal && (
        <ModalBox
          status={reviewModal}
          onClose={closeReviewModal}
          title="Add Review"
          closeIcon
          size="lg"
          custom
          customizedFooter={
            <div className="model_custom_footer">
              <button
                className="btn_size mx-4"
                ref={reviewButtonRef}
                onClick={addReviewHandler}
                style={{ backgroundColor: "#1976d2", color: "white" }}
              >
                Add Review
              </button>
            </div>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="form-group">
              <TextArea
                name="review"
                value={reviewFormData.review}
                onChange={handleReviewInputChange}
                required
                placeholder="Enter review text"
                className={`form-control ${reviewError.review ? "is-invalid" : ""}`}
                label="Review *"
                error={reviewError.review}
                rows={4}
              />
            </div>
            <div className="form-group">
              <Input
                type="number"
                name="rating"
                value={reviewFormData.rating}
                onChange={handleReviewInputChange}
                required
                min="1"
                max="5"
                placeholder="Rating"
                className={`form-control ${reviewError.rating ? "is-invalid" : ""}`}
                label="Rating"
                error={reviewError.rating}
              />
            </div>
            <div className="form-group">
              <Input
                type="text"
                name="userName"
                value={reviewFormData.userName}
                onChange={handleReviewInputChange}
                placeholder="User Name"
                className="form-control"
                label="User Name"
                maxLength={50}
              />
            </div>
            <div style={{ display: "flex", gap: 15, flexWrap: "wrap" }}>
              <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
                <Input
                  type="date"
                  name="customDate"
                  value={reviewFormData.customDate}
                  onChange={handleReviewInputChange}
                  className="form-control"
                  label="Date"
                />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
                <Input
                  type="time"
                  name="customTime"
                  value={reviewFormData.customTime}
                  onChange={handleReviewInputChange}
                  className="form-control"
                  label="Time"
                  error={reviewError.customTime}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
                Rating Type
              </label>
              <select
                name="ratingType"
                value={reviewFormData.ratingType}
                onChange={handleReviewInputChange}
                className="form-control"
              >
                <option value="chat">Chat</option>
                <option value="call">Call</option>
              </select>
            </div>
            <div className="form-group">
              <Input
                type="file"
                accept="image/*"
                onChange={handleReviewImageChange}
                className="form-control"
                label="User Image"
              />
              {reviewUserImagePreview && (
                <img
                  src={reviewUserImagePreview}
                  alt="User Preview"
                  style={{ maxWidth: 150, height: "auto", borderRadius: 6, border: "1px solid #eee", marginTop: 8 }}
                />
              )}
            </div>
            <div className="form-group">
              <TextArea
                name="reply"
                value={reviewFormData.reply}
                onChange={handleReviewInputChange}
                placeholder="Consultant Reply"
                className="form-control"
                label="Consultant Reply"
                rows={3}
              />
            </div>
          </div>
        </ModalBox>
      )}
      {confirmSubmittedAction && (
        <ModalBox
          status={Boolean(confirmSubmittedAction)}
          onClose={() => setConfirmSubmittedAction(null)}
          title={confirmSubmittedAction.title}
          description={confirmSubmittedAction.description}
          actionLoading={submittedActionLoading}
          custom
          customizedFooter={
            <div className="d-flex justify-content-center gap-3 w-100">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ minWidth: 96, fontWeight: 500 }}
                disabled={submittedActionLoading}
                onClick={() => setConfirmSubmittedAction(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-sm ${confirmSubmittedAction.actionClass || "btn-primary"}`}
                style={{ minWidth: 96, fontWeight: 500 }}
                disabled={submittedActionLoading}
                onClick={confirmSubmittedActionHandler}
              >
                {confirmSubmittedAction.actionLabel}
              </button>
            </div>
          }
        >
          {confirmSubmittedAction.type === "consultantBin" && (
            <div className="form-group mt-2">
              <TextArea
                value={consultantRemark}
                onChange={(e) => setConsultantRemark(e.target.value)}
                className="form-control"
                label="Bin Remark"
                required
                placeholder="Enter reason for moving consultant to bin"
                rows={3}
              />
            </div>
          )}
        </ModalBox>
      )}
    </>
  );
};

export default PreSelectedConsultant;
